import { bankWithdrawalProvider, bankWithdrawalAssets, matchesBankFundingAsset } from '../../util/BankWithdrawals'
import { observer } from 'mobx-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { Container } from '../../components/Animations'
import { PrimaryButton, SecondaryLinkButton } from '../../components/Button'
import {
  DashboardPage,
  EmptyState,
  InlinePill,
  PageActionRow,
  PageHeaderRow
} from '../../components/DashboardPrimitives'
import { DropdownContext, DropdownItem, DropdownSearchContainer, DropdownTextInput } from '../../components/Dropdown'
import { TextInput } from '../../components/Input'
import LoadingBar from '../../components/LoadingBar'
import PageHeader from '../../components/PageHeader'
import RecentTransactions from '../../components/RecentTransactionts'
import { Chevron } from '../../icons/Chevron'
import FiatRamps from '../../state/FiatRamps'
import Radom, {
  Customer,
  ExternalAccount,
  ICreateCustomer,
  Payout,
  PayoutAmountMode,
  PayoutFeeEstimate,
  PayoutFundingCurrency,
  WithdrawalAccount,
  BankWithdrawalEstimate,
  CreateCryptoPayoutEntity,
  PayoutCapabilitiesResponse,
  WithdrawalRequestWithAccountName
} from '../../state/Radom'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import { isCryptoExternalAccount, isEurIbanExternalAccount } from '../../util/ExternalAccounts'
import {
  ManagedPaymentMethod,
  getMethod,
  getPaymentMethods,
  methodNetworkName,
  methodTokenAddress,
  prodNetworks,
  testNetworks,
  unifiedAssetNetworkOptions
} from '../../util/Managed'
import { ROUTES, errorToast, floorAssetAmount, formatAssetAmount, shortAddress, successToast, useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import CreateWithdrawalAccountModal, { AccountType } from '../Funds/CreateWithdrawalAccountModal'
import CreateExternalAccountModal, { PayoutAccountCategory } from '../Customers/CreateExternalAccountModal'
import CustomerForm from '../../components/CustomerForm'
import Modal from '../../components/Modal'
import DestinationPicker from './DestinationPicker'
import EscapableDropdown from './EscapableDropdown'
import RecipientAvatar from './RecipientAvatar'
import {
  BackTextButton,
  BlockerNotice,
  CenteredWorkspace,
  FlowSurface,
  AmountCard,
  AmountColumn,
  AmountInput,
  AmountRow,
  AmountRowBody,
  AmountRowHeader,
  AmountRowLabel,
  AmountRowMeta,
  AmountSub,
  AssetChip,
  AssetChipNetwork,
  AssetChipText,
  AssetChipTicker,
  CounterAmount,
  DirectionGlyph,
  InlineError,
  MaxTextButton,
  QuoteNote,
  QuoteStack,
  RowDivider,
  SwapSidesButton,
  QuoteBox,
  QuoteLabel,
  QuoteLineButton,
  QuoteRow,
  QuoteValue,
  ReviewAmount,
  ReviewDetail,
  ReviewIdentity,
  ReviewName,
  SectionLabel,
  SelectionLabel,
  SelectionMeta,
  SelectionSubtext,
  SendLayout,
  SimpleMethodIcon,
  StepReveal,
  SuccessBadge,
  SuccessHeading,
  SuccessNote,
  SuccessPanel,
  SuccessSummary,
  QuickActionsRow,
  QuickActionPill,
  QuickActionGlyph,
  RecipientCard,
  RecipientCardTitle,
  RecipientCardHint,
  RecipientFieldsGrid,
  assetChipStyle,
  fundingTextTriggerStyle,
  ChoiceCard,
  ChoiceGrid,
  ChoiceHint,
  ChoiceList,
  ChoiceListItem,
  ChoiceTitle
} from './SendStyles'
import {
  FundingKind,
  FundingQuotePoint,
  OneOffKind,
  SendDestination,
  destinationDisplayName,
  destinationKey,
  destinationMode,
  fundingMatchesTyped,
  oneOffCandidateNetworks,
  quotedConversionRate,
  recipientForFunding,
  reconcileRecipientForFunding,
  withdrawalAccountSubtitle
} from './sendUtil'

/** The request a fee estimate answered, so a quote for other inputs never dresses up the current ones. */
interface FeeQuoteInputs {
  /** The rails and assets: flow, funding currency, funding balance and delivered asset. */
  pair: string
  delivery: string
  amount: string
  mode: PayoutAmountMode
}

/** Which panel occupies the form surface: edit, confirm, or post-send. */
type SendStep = 'form' | 'review' | 'success'

interface FundingAsset {
  network: string
  token?: string
  maxAmount: string
  treasuryId?: string
  /** Set when this balance buys the delivered asset at send time instead of being sent as is. */
  fundingCurrency?: PayoutFundingCurrency
  /** Set when this balance is converted into the delivered asset right before it is sent. */
  convert?: boolean
}

const fundingAssetKey = (asset: FundingAsset): string =>
  `${asset.network}:${asset.token ?? 'native'}:${asset.treasuryId ?? 'radom'}:${asset.fundingCurrency ?? 'same'}:${asset.convert ? 'convert' : 'direct'}`
const heldAssetKey = (asset: { network: string, token?: string }): string => `${asset.network}:${asset.token ?? 'native'}`

/**
 * The concrete rails a selected destination resolves to. Own accounts book as
 * withdrawals (crypto rail, the pooled EUR cash rail, or the legacy cash
 * rail); customers and one-off addresses book as payouts.
 */
type SendFlow =
  | { kind: 'idle' }
  | { kind: 'blocked', reason: string }
  | { kind: 'customerPending', customer: Customer }
  | { kind: 'cryptoWithdrawal', account: WithdrawalAccount, network: string }
  | { kind: 'ivyCashWithdrawal', account: WithdrawalAccount }
  | { kind: 'bankWithdrawal', account: WithdrawalAccount, currency: 'EUR' | 'USD' }
  | {
    kind: 'cryptoPayout'
    payoutAddress: string
    requiredNetwork?: string
    customer?: Customer
    externalAccount?: ExternalAccount
  }
  | { kind: 'fiatPayout', customer: Customer, externalAccount: ExternalAccount }
  /** A pasted address or IBAN that is saved (on a contact or as an own account) before it is paid. */
  | { kind: 'saveDestination', value: string, oneOffKind: OneOffKind, candidates: string[] }

/** Everything the in-place success panel needs after the form resets. */
interface SuccessInfo {
  heading: string
  amountLabel: string
  recipientName: string
  recipientDetail: string
  note: string
  historyHref: string
  historyLabel: string
}

// Bank payouts fund a job at the settlement desk, which rejects deposits below
// this size. The quote carries the live value; this is the API's default.
const BANK_PAYOUT_MINIMUM_FUNDING = 30

// USDC on Ethereum: what a converted balance becomes before it funds a bank payout.
const ETHEREUM_USDC_TOKEN = '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48'
// Networks a payout delivers on by itself. Elsewhere the API routes the funding
// balance through the exchange, so the request names that balance.
const PLAIN_PAYOUT_NETWORKS = new Set(['Ethereum', 'Tron'])

const formatQuoteAmount = (value?: number | string | null, decimals = 6): string => {
  if (value === undefined || value === null || value === '') return '-'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  })
}

// The registry's fiat entries live under the "Fiat" network; not all of them carry the flag.
const isFiatMethod = (method: ManagedPaymentMethod): boolean => !!method.isFiat || method.name === 'Fiat'

// Fees are charged on top of the amount for every send this page creates: the
// recipient gets the full amount and the organization's debit is the amount
// plus the fee. A deducted fee would come out of what the recipient gets.
const DEDUCT_FEES = false

/** "1 USDC ≈ 0.9993 USDT": four decimals above one, four significant digits below. */
const formatRate = (value: number): string => value >= 1
  ? value.toLocaleString(undefined, { maximumFractionDigits: 4 })
  : value.toLocaleString(undefined, { maximumSignificantDigits: 4 })

/** The number a row displays, as an input value: rounded the same way, without thousands separators. */
const plainAmount = (value: number, ticker?: string, fiat = false): string => fiat
  ? value.toFixed(2).replace(/\.?0+$/, '')
  : formatAssetAmount(value, ticker).replace(/,/g, '')

const SwapArrows = (): ReactElement => (
  <svg
    viewBox='0 0 16 16'
    width='14'
    height='14'
    aria-hidden='true'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.6'
    strokeLinecap='round'
    strokeLinejoin='round'>
    <path d='M5 2.5v11M2.5 11 5 13.5 7.5 11' />
    <path d='M11 13.5v-11M8.5 5 11 2.5 13.5 5' />
  </svg>
)

const DownArrow = (): ReactElement => (
  <svg
    viewBox='0 0 16 16'
    width='14'
    height='14'
    aria-hidden='true'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.6'
    strokeLinecap='round'
    strokeLinejoin='round'>
    <path d='M8 2.5v11M4.5 10 8 13.5 11.5 10' />
  </svg>
)

const externalAccountLabel = (account: ExternalAccount): string => {
  if (account.crypto) {
    return `${account.crypto.network} wallet · ${shortAddress(account.crypto.address, 6)}`
  }
  if (account.iban) {
    return `EUR account · IBAN ··${account.iban.last4}`
  }
  return account.bankName || 'Bank account'
}

const CheckMark = (): ReactElement => (
  <svg viewBox='0 0 24 24' width='26' height='26' aria-hidden='true'>
    <path
      d='m5 12.5 4.5 4.5L19 7.5'
      fill='none'
      stroke='currentColor'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round' />
  </svg>
)

const Send = observer((): ReactElement => {
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const canReadBalance = has('ReadBalance')
  const canReadAccounts = has('ReadWithdrawalAccount')
  const canWriteAccounts = has('WriteWithdrawalAccount')
  const canReadCustomers = has('ReadCustomers')
  const canReadExternalAccounts = has('ReadExternalAccount')
  const canSaveIbanRecipients = has('WriteCustomers', 'WriteExternalAccount', 'ReadExternalAccount')
  const canReadWithdrawals = has('ReadWithdrawal')
  const canReadPayouts = has('ReadPayouts')
  const canWriteWithdrawal = has('WriteWithdrawal')
  const canWritePayouts = has('WritePayouts')
  const canUseView = canWriteWithdrawal || canWritePayouts || canReadWithdrawals || canReadPayouts

  const [step, setStep] = useState<SendStep>('form')
  const [successInfo, setSuccessInfo] = useState<SuccessInfo>()
  const [destination, setDestination] = useState<SendDestination>()
  // Which in-flow "save a destination" modal is open, if any. Checked against
  // undefined everywhere: AccountType.Crypto is 0 and would fail truthiness.
  const [createAccountType, setCreateAccountType] = useState<AccountType>()
  // "Add your own account" opens the account drawer with its type chooser.
  const [isOwnAccountChooserOpen, setIsOwnAccountChooserOpen] = useState(false)
  // "New recipient" creates a contact, then goes straight to a way to pay them.
  const [isNewRecipientOpen, setIsNewRecipientOpen] = useState(false)
  const [newRecipient, setNewRecipient] = useState<Customer | null>(null)
  const [accounts, setAccounts] = useState<WithdrawalAccount[]>([])
  const [customers, setCustomers] = useState<Customer[]>([])
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false)
  const [externalAccounts, setExternalAccounts] = useState<ExternalAccount[]>([])
  const [isLoadingExternalAccounts, setIsLoadingExternalAccounts] = useState(false)
  const [selectedExternalAccountId, setSelectedExternalAccountId] = useState<string>()
  const [selectedFundingKey, setSelectedFundingKey] = useState<string>()
  // Crypto payouts separate what the recipient gets from what you pay with.
  const [selectedDeliveryKey, setSelectedDeliveryKey] = useState<string>()
  const [payoutCapabilities, setPayoutCapabilities] = useState<PayoutCapabilitiesResponse | null>(null)
  // A stablecoin-funded payout is quoted (created) on the way to review and confirmed on Send.
  const [hedgedQuote, setHedgedQuote] = useState<Payout | null>(null)
  const hedgedQuoteRef = useRef<Payout | null>(null)
  const [isQuotingHedged, setIsQuotingHedged] = useState(false)
  // A quote the merchant walks away from is cancelled so it does not sit in
  // history for two hours as a payout waiting to be sent. Fire and forget: a
  // cancel that fails only leaves the quote to expire on its own.
  const discardHedgedQuote = (): void => {
    const quote = hedgedQuoteRef.current
    hedgedQuoteRef.current = null
    setHedgedQuote(null)
    if (quote) Radom.cancelPayout(quote.id).catch(() => {})
  }
  useEffect(() => () => { discardHedgedQuote() }, [])
  // Convert-then-send: which held assets the exchange converts into what, and the priced conversion for review.
  const [swapTargets, setSwapTargets] = useState<Record<string, Array<{ network: string, token?: string | null }>>>({})
  const [targetsPending, setTargetsPending] = useState(false)
  const [convertQuote, setConvertQuote] = useState<{ fromAmount: string, quotedAmount: string } | null>(null)
  // /send?asset=BTC preselects that balance once the destination allows it.
  const requestedFundingTicker = useRef<string | undefined>(undefined)
  const [assetSearch, setAssetSearch] = useState('')
  const [amount, setAmount] = useState('')
  const [amountMode, setAmountMode] = useState<PayoutAmountMode>('recipient')
  const [payWithAmount, setPayWithAmount] = useState('')
  // A pasted destination being saved: who it belongs to, and which contact
  // when it is someone the merchant pays.
  const [pendingSave, setPendingSave] = useState<{
    value: string
    oneOffKind: OneOffKind
    candidates: string[]
    target: 'contact' | 'own'
  }>()
  const [saveContactSearch, setSaveContactSearch] = useState('')
  const [feeEstimate, setFeeEstimate] = useState<PayoutFeeEstimate | null>(null)
  const [feeEstimateFor, setFeeEstimateFor] = useState<FeeQuoteInputs>()
  const [isEstimatingFee, setIsEstimatingFee] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [quoteExpanded, setQuoteExpanded] = useState(false)
  const [recentWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequestWithAccountName[]>([])
  const [recentPayouts, setRecentPayouts] = useState<Payout[]>([])
  const [isLoadingActivity, setIsLoadingActivity] = useState(false)

  const amountInputRef = useRef<HTMLInputElement | null>(null)
  const pendingAmountFocus = useRef(false)
  // A contact's delivery account asked for by a deep link, applied once the
  // contact's accounts have loaded.
  const requestedExternalAccountId = useRef<string | undefined>(undefined)
  const reviewActionsRef = useRef<HTMLDivElement | null>(null)
  // The address or IBAN just saved on a contact, so its account is picked once
  // the contact's accounts reload.
  const pendingSelectValue = useRef<string | undefined>(undefined)

  // One key per logical cash submission: stable across retries after an error
  // (so a timed-out request replays instead of double-paying), fresh after
  // success. Mirrors the existing withdrawal modal behavior.
  const cashSubmissionKey = useRef(crypto.randomUUID())

  const bankAvailability = User.organization?.bankWithdrawals
  const [bankEstimate, setBankEstimate] = useState<BankWithdrawalEstimate | null>(null)
  const [bankQuoteError, setBankQuoteError] = useState('')
  const [isQuotingBank, setIsQuotingBank] = useState(false)

  /** Loads both recipient lists; returns the fresh accounts so callers can
   *  pick out a just-created one without a second fetch. */
  const loadRecipients = async (): Promise<WithdrawalAccount[]> => {
    setIsLoadingRecipients(true)
    const networks = testMode ? testNetworks : prodNetworks
    const [accountsResult, customersResult] = await Promise.allSettled([
      canReadAccounts ? Radom.listWithdrawalAccounts() : Promise.resolve([] as WithdrawalAccount[]),
      canReadCustomers
        ? Radom.listCustomers(0, 100, networks)
        : Promise.resolve({ data: [] as Customer[], total: 0 })
    ])

    let loadedAccounts: WithdrawalAccount[] = []
    if (accountsResult.status === 'fulfilled') {
      loadedAccounts = accountsResult.value
      setAccounts(loadedAccounts)
    } else {
      console.error('Failed to load withdrawal accounts', accountsResult.reason)
      setAccounts([])
    }

    if (customersResult.status === 'fulfilled') {
      setCustomers(customersResult.value.data)
    } else {
      console.error('Failed to load customers', customersResult.reason)
      setCustomers([])
    }

    setIsLoadingRecipients(false)
    return loadedAccounts
  }

  // In-flow account creation: once the modal saves, the new account becomes
  // the selected destination so the user lands back mid-flow rather than at
  // an empty picker. The modal doesn't hand back the created entity, so the
  // reloaded list is diffed against the ids known beforehand, falling back to
  // the newest account by creation date.
  const handleAccountCreated = async (): Promise<void> => {
    const knownIds = new Set(accounts.map(a => a.id))
    setCreateAccountType(undefined)
    setIsOwnAccountChooserOpen(false)
    const freshAccounts = await loadRecipients()
    const newestFirst = [...freshAccounts].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    const created = newestFirst.find(a => !knownIds.has(a.id)) ?? newestFirst[0]
    if (created) {
      pendingAmountFocus.current = true
      setDestination({ kind: 'ownAccount', account: created })
    }
  }

  const loadActivity = async (): Promise<void> => {
    setIsLoadingActivity(true)
    const [withdrawalsResult, payoutsResult] = await Promise.allSettled([
      canReadWithdrawals
        ? Radom.listWithdrawals(0, 25, testMode ? testNetworks : prodNetworks)
        : Promise.resolve({ data: [] as WithdrawalRequestWithAccountName[], total: 0 }),
      canReadPayouts
        ? Radom.listPayouts(0, 6, testMode, [], false)
        : Promise.resolve({ data: [] as Payout[], total: 0 })
    ])

    setRecentWithdrawals(withdrawalsResult.status === 'fulfilled' ? withdrawalsResult.value.data : [])
    setRecentPayouts(payoutsResult.status === 'fulfilled' ? payoutsResult.value.data : [])
    if (withdrawalsResult.status === 'rejected') {
      console.error('Failed to load recent withdrawals', withdrawalsResult.reason)
    }
    if (payoutsResult.status === 'rejected') {
      console.error('Failed to load recent payouts', payoutsResult.reason)
    }
    setIsLoadingActivity(false)
  }

  useEffect(() => {
    if (!permissionsLoaded) return
    if (canReadBalance) {
      void Treasury.loadBalances()
    }
    void loadRecipients()
    void loadActivity()
  }, [permissionsLoaded, testMode])

  // Deep links land here with the destination already chosen, so a contact's
  // page or the saved-accounts list is one click away from the amount step:
  // /send?customer=<id>[&account=<external account id>] pays a contact and
  // /send?account=<withdrawal account id> sends to one of the merchant's own
  // saved accounts. The params are consumed once so a refresh or a cleared
  // picker does not re-apply them.
  useEffect(() => {
    if (!permissionsLoaded) return
    const customerId = searchParams.get('customer')
    const accountId = searchParams.get('account')
    const asset = searchParams.get('asset')
    if (asset) requestedFundingTicker.current = asset.toUpperCase()
    if (!customerId && !accountId && !asset) return
    setSearchParams({}, { replace: true })
    if (!customerId && !accountId) return

    if (customerId) {
      if (!canReadCustomers) return
      requestedExternalAccountId.current = accountId ?? undefined
      Radom.getCustomer(customerId)
        .then(customer => {
          pendingAmountFocus.current = true
          setDestination({ kind: 'customer', customer })
        })
        .catch(err => {
          requestedExternalAccountId.current = undefined
          errorToast(err.response?.data?.error || 'That contact could not be loaded')
        })
      return
    }

    if (!canReadAccounts) return
    Radom.listWithdrawalAccounts()
      .then(loaded => {
        const account = loaded.find(a => a.id === accountId)
        if (!account) {
          errorToast('That saved account could not be found')
          return
        }
        pendingAmountFocus.current = true
        setDestination({ kind: 'ownAccount', account })
      })
      .catch(err => errorToast(err.response?.data?.error || 'That saved account could not be loaded'))
  }, [permissionsLoaded])

  // A different recipient or delivery account means a different payout: any
  // quote priced for the previous one is dropped.
  useEffect(() => { discardHedgedQuote() }, [destination, selectedExternalAccountId])

  // Selecting a destination resets the details step; customers additionally
  // load their saved delivery accounts.
  useEffect(() => {
    setAmount('')
    setPendingSave(undefined)
    setSaveContactSearch('')
    setAmountMode('recipient')
    setPayWithAmount('')
    setFeeEstimate(null)
    setQuoteExpanded(false)
    setSelectedFundingKey(undefined)
    setSelectedExternalAccountId(undefined)
    setExternalAccounts([])

    if (destination?.kind === 'customer' && canReadExternalAccounts) {
      setIsLoadingExternalAccounts(true)
      Radom.listExternalAccounts(destination.customer.id)
        .then(loaded => {
          setExternalAccounts(loaded)
          const requested = requestedExternalAccountId.current
          requestedExternalAccountId.current = undefined
          const deliverable = loaded.find(a =>
            a.id === requested && (isCryptoExternalAccount(a) || isEurIbanExternalAccount(a)))
          if (deliverable) {
            setSelectedExternalAccountId(deliverable.id)
          }
        })
        .catch(err => {
          console.error('Failed to load customer accounts', err)
          setExternalAccounts([])
        })
        .finally(() => setIsLoadingExternalAccounts(false))
    }
  }, [destination])

  // Keyboard flow: once a destination is picked the amount input takes focus,
  // as soon as it exists (customers first resolve a delivery account).
  useEffect(() => {
    if (pendingAmountFocus.current && amountInputRef.current) {
      pendingAmountFocus.current = false
      amountInputRef.current.focus()
    }
  })

  // Review panel: focus the Send button so Enter submits, Escape goes back.
  useEffect(() => {
    if (step !== 'review') return

    reviewActionsRef.current?.querySelector('button')?.focus()
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !isSubmitting) {
        pendingAmountFocus.current = true
        discardHedgedQuote()
        setConvertQuote(null)
        setStep('form')
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [step, isSubmitting])

  // Which destinations a stablecoin balance can buy into, straight from the exchange routing.
  useEffect(() => {
    if (!canWritePayouts || testMode) {
      setPayoutCapabilities(null)
      return
    }
    let cancelled = false
    Radom.getPayoutCapabilities()
      .then(caps => { if (!cancelled) setPayoutCapabilities(caps) })
      .catch(err => {
        console.error('Failed to load payout capabilities', err)
        if (!cancelled) setPayoutCapabilities(null)
      })
    return () => { cancelled = true }
  }, [canWritePayouts, testMode])

  const fundingAssets = useMemo((): FundingAsset[] => {
    const all = [...Treasury.managedBalances, ...Treasury.privateManagedBalances]
    return all
      .filter(b => Number(b.balance) > 0)
      .flatMap((b): FundingAsset[] => {
        // Unified-treasury rows carry an asset ticker and no network, but a
        // send still settles on a concrete rail: expand one unified row into
        // one funding option per settlement network the registry knows for
        // that asset. Every option carries the full unified balance as
        // maxAmount; the backend enforces the real spendable cap when the
        // send executes.
        if (b.asset) {
          if (testMode) return []
          return unifiedAssetNetworkOptions(b.asset)
            .map(option => ({ ...option, maxAmount: b.balance, treasuryId: b.treasuryId }))
        }
        if (b.network === undefined) return []
        const method = getMethod(b.network, b.token)
        if (b.network === 'Fiat' || method.isFiat) return []
        if (testMode ? !method.isTestnet : !!method.isTestnet) return []
        return [{ network: b.network, token: b.token, maxAmount: b.balance, treasuryId: b.treasuryId }]
      })
  }, [Treasury.managedBalances, Treasury.privateManagedBalances, testMode])

  const deliveryOptions = useMemo(
    () => externalAccounts.filter(a => isCryptoExternalAccount(a) || isEurIbanExternalAccount(a)),
    [externalAccounts]
  )
  const selectedExternalAccount = deliveryOptions.find(a => a.id === selectedExternalAccountId)

  // Auto-pick the only delivery option so a single-account customer needs no
  // extra click.
  useEffect(() => {
    if (destination?.kind !== 'customer' || selectedExternalAccountId) return
    const wanted = pendingSelectValue.current
    const saved = wanted
      ? deliveryOptions.find(a => a.crypto?.address === wanted || (a.iban && wanted.endsWith(a.iban.last4)))
      : undefined
    if (saved) {
      pendingSelectValue.current = undefined
      setSelectedExternalAccountId(saved.id)
    } else if (deliveryOptions.length === 1) {
      setSelectedExternalAccountId(deliveryOptions[0].id)
    }
  }, [deliveryOptions, destination, selectedExternalAccountId])

  const flow = useMemo((): SendFlow => {
    if (!destination) return { kind: 'idle' }

    if (destination.kind === 'ownAccount') {
      const account = destination.account
      const crypto = account.accountDetails.Crypto
      if (crypto) {
        return { kind: 'cryptoWithdrawal', account, network: crypto.network }
      }

      const fiat = account.accountDetails.Fiat
      const isIban = !!fiat?.accountType.iban
      const isUs = !!fiat?.accountType.us

      const provider = bankWithdrawalProvider(account, bankAvailability, FiatRamps.cashWithdrawalEnabled)
      if (provider === 'bastion') {
        return { kind: 'bankWithdrawal', account, currency: isUs ? 'USD' : 'EUR' }
      }
      if (isIban && provider === 'ivy') return { kind: 'ivyCashWithdrawal', account }
      if (isIban || isUs) {
        return { kind: 'blocked', reason: 'Bank withdrawals are not enabled for this account. Contact support to enable them.' }
      }

      return { kind: 'blocked', reason: 'This saved account type isn’t supported for sending yet.' }
    }

    if (destination.kind === 'customer') {
      const customer = destination.customer
      if (!canReadExternalAccounts) {
        return {
          kind: 'blocked',
          reason: 'Your role can’t read contact accounts, so there’s nowhere to deliver this payout. Ask an admin for the ReadExternalAccount permission.'
        }
      }
      if (isLoadingExternalAccounts) {
        return { kind: 'customerPending', customer }
      }
      if (deliveryOptions.length === 0) {
        return {
          kind: 'blocked',
          reason: externalAccounts.length > 0
            ? 'This contact’s saved accounts aren’t supported yet. Payouts currently deliver to EUR accounts and wallets.'
            : 'This contact has no saved bank account or wallet yet. Add one from their page under Contacts, then come back.'
        }
      }
      if (!selectedExternalAccount) {
        return { kind: 'customerPending', customer }
      }
      if (selectedExternalAccount.crypto) {
        return {
          kind: 'cryptoPayout',
          payoutAddress: selectedExternalAccount.crypto.address,
          requiredNetwork: selectedExternalAccount.crypto.network,
          customer,
          externalAccount: selectedExternalAccount
        }
      }
      return { kind: 'fiatPayout', customer, externalAccount: selectedExternalAccount }
    }

    // A pasted destination is saved before it is paid: on a contact (someone
    // the merchant pays) or as one of the merchant's own accounts (a
    // withdrawal). The saved account carries the name, the network and the
    // bank details every send needs.
    return {
      kind: 'saveDestination',
      value: destination.value,
      oneOffKind: destination.oneOffKind,
      candidates: oneOffCandidateNetworks(destination.oneOffKind, destination.value)
    }
  }, [
    destination,
    canWriteAccounts,
    bankAvailability,
    FiatRamps.cashWithdrawalEnabled,
    canReadExternalAccounts,
    isLoadingExternalAccounts,
    deliveryOptions,
    externalAccounts.length,
    selectedExternalAccount,
    canSaveIbanRecipients
  ])

  const expectedUsdcNetwork = testMode ? 'SepoliaTestnet' : 'Ethereum'
  const unifiedTreasury = User.organization?.features?.isUnifiedTreasuryEnabled === true
  const conversionEnabled = User.organization?.features?.isConversionEnabled === true
  // Conversion-funded payouts follow the conversion flag; FBO organizations do not get them.
  const stablecoinPayoutsEnabled = conversionEnabled && User.organization?.features?.isFboEnabled !== true
  const canConvert = conversionEnabled && !testMode && has('WriteSwap')
  const isCryptoDelivery = flow.kind === 'cryptoPayout' || flow.kind === 'cryptoWithdrawal'

  // What a crypto send can deliver: every asset the destination network carries.
  const deliveryMethods = useMemo((): ManagedPaymentMethod[] => {
    if (flow.kind !== 'cryptoPayout' && flow.kind !== 'cryptoWithdrawal') return []
    const fixedNetwork = flow.kind === 'cryptoWithdrawal' ? flow.network : flow.requiredNetwork
    return getPaymentMethods()
      .filter(m => !m.isFiat && !m.isDisabled && !m.isHidden)
      .filter(m => testMode ? !!m.isTestnet : !m.isTestnet)
      .filter(m => fixedNetwork ? methodNetworkName(m) === fixedNetwork : false)
  }, [flow, testMode])
  // The network is known from the saved wallet; it only needs naming when a pasted address could live on several.
  const deliveryNetworkIsFixed = flow.kind === 'cryptoWithdrawal' ||
    (flow.kind === 'cryptoPayout' && (!!flow.requiredNetwork || new Set(deliveryMethods.map(methodNetworkName)).size <= 1))

  // Which of the held assets the exchange converts into what, asked once per
  // asset. Keyed on the set of held assets as a string so balance refreshes that
  // rebuild the list do not ask again.
  const heldAssetKeys = useMemo(
    () => [...new Set(fundingAssets.filter(a => !a.treasuryId).map(heldAssetKey))].sort().join('|'),
    [fundingAssets])
  const eurRecipientFlow = flow.kind === 'ivyCashWithdrawal' || flow.kind === 'fiatPayout'
  useEffect(() => {
    if (!canConvert || !(isCryptoDelivery || eurRecipientFlow) || !heldAssetKeys) return
    const missing = heldAssetKeys.split('|')
      .filter(key => !(key in swapTargets))
      .map(key => {
        const [network, token] = key.split(':')
        return { key, network, token: token === 'native' ? undefined : token }
      })
    if (missing.length === 0) return
    let cancelled = false
    setTargetsPending(true)
    // One request for every held asset: the registry answers them all at once,
    // so the list fills in one step instead of a few assets at a time.
    void (async () => {
      let entries: Array<[string, Array<{ network: string, token?: string | null }>]>
      try {
        const response = await Radom.getSwapSupportedTargetsFor(missing.map(a => ({ network: a.network, token: a.token })))
        const byKey = new Map((response.targets ?? []).map(t => [heldAssetKey({ network: t.from.network, token: t.from.token ?? undefined }), t.assets]))
        entries = missing.map(a => [a.key, byKey.get(a.key) ?? []])
      } catch (err) {
        console.error('Failed to load conversion targets', err)
        entries = missing.map(a => [a.key, []])
      }
      if (cancelled) return
      setSwapTargets(prev => ({ ...prev, ...Object.fromEntries(entries) }))
      setTargetsPending(false)
    })()
    return () => { cancelled = true }
  }, [canConvert, isCryptoDelivery, eurRecipientFlow, heldAssetKeys])
  const deliveryKey = (method: ManagedPaymentMethod): string =>
    `${methodNetworkName(method)}:${methodTokenAddress(method) ?? 'native'}`
  const selectedDeliveryMethod = deliveryMethods.find(m => deliveryKey(m) === selectedDeliveryKey)
  const ownBalanceOf = (method: ManagedPaymentMethod): number => fundingAssets
    .filter(f => !f.treasuryId && f.network === methodNetworkName(method) &&
      getMethod(f.network, f.token).ticker === method.ticker)
    .reduce((sum, f) => sum + Number(f.maxAmount), 0)
  const hedgeCurrenciesFor = (method: ManagedPaymentMethod): PayoutFundingCurrency[] => {
    if (!stablecoinPayoutsEnabled || testMode) return []
    const token = (methodTokenAddress(method) ?? '').toLowerCase()
    return (payoutCapabilities?.fundedDestinations ?? [])
      .filter(cap => cap.network === methodNetworkName(method) && (cap.token ?? '').toLowerCase() === token)
      .map(cap => cap.fundingCurrency)
      .filter(currency => currency !== method.ticker)
  }

  // Everything a held asset converts into, across all held assets.
  const reachableTargets = useMemo(() => {
    const keys = new Set<string>()
    Object.values(swapTargets).forEach(list => list.forEach(t => keys.add(`${t.network}:${(t.token ?? '').toLowerCase()}`)))
    return keys
  }, [swapTargets])
  // Delivered assets worth offering: held, buyable from a stablecoin, or convertible from something held.
  const deliveryChoices = useMemo((): ManagedPaymentMethod[] => {
    const fundable = deliveryMethods.filter(m => ownBalanceOf(m) > 0 || hedgeCurrenciesFor(m).length > 0 ||
      (canConvert && reachableTargets.has(`${methodNetworkName(m)}:${(methodTokenAddress(m) ?? '').toLowerCase()}`)))
    return fundable.length > 0 ? fundable : deliveryMethods
  }, [deliveryMethods, fundingAssets, payoutCapabilities, canConvert, reachableTargets])

  // Default to the asset you hold most of on that network, else the network's own coin.
  useEffect(() => {
    if (selectedDeliveryMethod || deliveryChoices.length === 0) return
    const held = [...deliveryChoices].sort((a, b) => ownBalanceOf(b) - ownBalanceOf(a))[0]
    const fallback = deliveryChoices.find(m => !m.hostChain) ?? deliveryChoices[0]
    setSelectedDeliveryKey(deliveryKey(ownBalanceOf(held) > 0 ? held : fallback))
  }, [deliveryChoices, selectedDeliveryMethod, fundingAssets])

  const eligibleFundingAssets = useMemo((): FundingAsset[] => {
    // Unified orgs pick funding by asset, not by rail: collapse the expanded
    // per-network options back to one row per ticker (the canonical variant
    // lists first). Network-pinned flows keep their concrete rail.
    // One row per asset: the balance is named, never the rail it sits on. Where an
    // asset is held on several rails the largest balance is the one offered.
    const dedupeByTicker = (list: FundingAsset[]): FundingAsset[] => {
      const best = new Map<string, FundingAsset>()
      const privateTreasury: FundingAsset[] = []
      for (const a of list) {
        if (a.treasuryId) {
          privateTreasury.push(a)
          continue
        }
        const ticker = getMethod(a.network, a.token).ticker
        const current = best.get(ticker)
        if (!current || Number(a.maxAmount) > Number(current.maxAmount)) best.set(ticker, a)
      }
      return [...best.values(), ...privateTreasury]
    }
    switch (flow.kind) {
    case 'bankWithdrawal':
      return fundingAssets.filter(a => !a.treasuryId &&
        matchesBankFundingAsset(a, bankWithdrawalAssets(bankAvailability, flow.currency)))
    // Cash out to a EUR account from any balance the off-ramp converts:
    // stablecoins for everyone, BTC/ETH/SOL and the rest for business recipients.
    case 'ivyCashWithdrawal':
    case 'fiatPayout': {
      const stables = dedupeByTicker(fundingAssets.filter(a => {
        if (a.treasuryId) return false
        const method = getMethod(a.network, a.token)
        const isUsdc = a.network === expectedUsdcNetwork && method.ticker === 'USDC'
        const isUsdt = !testMode && a.network === 'Ethereum' && method.ticker === 'USDT'
        const isBscStable = !testMode && a.network === 'BNB' &&
          (method.ticker === 'USDC' || method.ticker === 'USDT')
        return isUsdc || isUsdt || isBscStable
      }))
      // ...or any other balance the exchange converts into USDC as the payout goes out.
      const offered = new Set(stables.map(a => getMethod(a.network, a.token).ticker))
      const converted = canConvert
        ? dedupeByTicker(fundingAssets.filter(a => {
          if (a.treasuryId) return false
          const ticker = getMethod(a.network, a.token).ticker
          if (offered.has(ticker) || ticker === 'USDC' || ticker === 'USDT') return false
          return (swapTargets[heldAssetKey(a)] ?? [])
            .some(t => t.network === 'Ethereum' && (t.token ?? '').toLowerCase() === ETHEREUM_USDC_TOKEN)
        })).map(a => ({ ...a, convert: true }))
        : []
      return [...stables, ...converted]
    }
    case 'cryptoWithdrawal':
    case 'cryptoPayout': {
      if (!selectedDeliveryMethod) return []
      const deliveryNetwork = methodNetworkName(selectedDeliveryMethod)
      const deliveryToken = (methodTokenAddress(selectedDeliveryMethod) ?? '').toLowerCase()
      // Send the asset itself from a balance on that network...
      const sameAsset = dedupeByTicker(fundingAssets.filter(a =>
        a.network === deliveryNetwork && getMethod(a.network, a.token).ticker === selectedDeliveryMethod.ticker))
      // ...or buy it at send time from a stablecoin balance the exchange can route (payouts only)...
      const stableFunded = (flow.kind === 'cryptoPayout' ? hedgeCurrenciesFor(selectedDeliveryMethod) : []).flatMap((currency): FundingAsset[] => {
        const rows = fundingAssets.filter(a => !a.treasuryId &&
          getMethod(a.network, a.token).ticker === currency && (unifiedTreasury || a.network === 'Ethereum'))
        const row = rows.find(a => a.network === 'Ethereum') ?? rows[0]
        return row ? [{ ...row, fundingCurrency: currency }] : []
      })
      // ...or convert any other balance into it right before it goes out.
      const offered = new Set([...sameAsset, ...stableFunded].map(a => getMethod(a.network, a.token).ticker))
      const converted = canConvert
        ? dedupeByTicker(fundingAssets.filter(a => {
          if (a.treasuryId) return false
          const ticker = getMethod(a.network, a.token).ticker
          if (ticker === selectedDeliveryMethod.ticker || offered.has(ticker)) return false
          return (swapTargets[heldAssetKey(a)] ?? [])
            .some(t => t.network === deliveryNetwork && (t.token ?? '').toLowerCase() === deliveryToken)
        })).map(a => ({ ...a, convert: true }))
        : []
      return [...sameAsset, ...stableFunded, ...converted]
    }
    default:
      return []
    }
  }, [flow, fundingAssets, expectedUsdcNetwork, testMode, unifiedTreasury, selectedDeliveryMethod,
    payoutCapabilities, stablecoinPayoutsEnabled, canConvert, swapTargets])

  const selectedFundingAsset = eligibleFundingAssets.find(a => fundingAssetKey(a) === selectedFundingKey)

  // Propose a funding balance once the destination narrows the choices: the
  // largest stable for cash, otherwise the largest eligible balance. An
  // explicit pick always wins.
  useEffect(() => {
    if (selectedFundingAsset || eligibleFundingAssets.length === 0) return

    const requested = requestedFundingTicker.current
    if (requested) {
      const match = eligibleFundingAssets.find(a => getMethod(a.network, a.token).ticker === requested)
      if (match) {
        requestedFundingTicker.current = undefined
        setSelectedFundingKey(fundingAssetKey(match))
        return
      }
    }

    const pool = flow.kind === 'ivyCashWithdrawal' || flow.kind === 'fiatPayout'
      ? eligibleFundingAssets.filter(a => {
        const ticker = getMethod(a.network, a.token).ticker
        return ticker === 'USDC' || ticker === 'USDT'
      })
      : isCryptoDelivery
        ? eligibleFundingAssets.filter(a => !a.fundingCurrency && !a.convert)
        : eligibleFundingAssets

    const best = [...(pool.length > 0 ? pool : eligibleFundingAssets)]
      .sort((a, b) => Number(b.maxAmount) - Number(a.maxAmount))[0]
    if (best) {
      setSelectedFundingKey(fundingAssetKey(best))
    }
  }, [eligibleFundingAssets, selectedFundingAsset, flow.kind])

  const selectedFundingMethod = selectedFundingAsset
    ? getMethod(selectedFundingAsset.network, selectedFundingAsset.token)
    : undefined

  const cashFundingCurrency: PayoutFundingCurrency = (():
  PayoutFundingCurrency => {
    if (!selectedFundingMethod) return 'USDC'
    const ticker = selectedFundingMethod.ticker
    // BNB Chain stablecoins fund over the BEP-20 rail.
    if (selectedFundingAsset?.network === 'BNB' && ticker === 'USDC') return 'USDC-BSC'
    if (selectedFundingAsset?.network === 'BNB' && ticker === 'USDT') return 'USDT-BSC'
    if (ticker === 'USDT') return 'USDT'
    // Any other balance is converted into USDC by the payout itself.
    return 'USDC'
  })()

  const isEurRecipient = flow.kind === 'ivyCashWithdrawal' || flow.kind === 'fiatPayout'
  const needsQuote = isEurRecipient || flow.kind === 'cryptoPayout'

  // Everything a fee estimate depends on besides the amount and the side typed.
  const feeQuotePair = `${flow.kind}|${cashFundingCurrency}|${selectedFundingKey ?? ''}|${selectedDeliveryKey ?? ''}`

  // Live quote for flows the fee endpoint knows about, debounced while typing.
  useEffect(() => {
    if (!needsQuote || !amount || Number(amount) <= 0) {
      setFeeEstimate(null)
      setFeeEstimateFor(undefined)
      setIsEstimatingFee(false)
      return
    }
    if (flow.kind === 'cryptoPayout' && !selectedFundingMethod) {
      setFeeEstimate(null)
      setFeeEstimateFor(undefined)
      return
    }

    const requested: FeeQuoteInputs = { pair: feeQuotePair, delivery: selectedDeliveryKey ?? '', amount, mode: amountMode }
    let cancelled = false
    setIsEstimatingFee(true)
    const timeout = window.setTimeout(() => {
      const request = flow.kind === 'ivyCashWithdrawal'
        ? Radom.getPayoutFee({
          currency: 'EUR',
          amount,
          amountMode,
          accountType: flow.account.accountDetails.Fiat?.accountOwnerType === 'Individual' ? 'individual' : 'business',
          fundingCurrency: cashFundingCurrency,
          fundingNetwork: convertFunding?.network,
          fundingToken: convertFunding?.token
        })
        : flow.kind === 'fiatPayout'
          ? Radom.getPayoutFee({
            currency: 'EUR',
            amount,
            accountType: flow.externalAccount.accountOwnerType || flow.customer.accountType || 'individual',
            fundingCurrency: cashFundingCurrency,
            fundingNetwork: convertFunding?.network,
            fundingToken: convertFunding?.token
          })
          : Radom.getPayoutFee({
            currency: 'USD',
            amount,
            network: selectedDeliveryMethod ? methodNetworkName(selectedDeliveryMethod) : undefined,
            token: selectedDeliveryMethod?.ticker,
            deliveryToken: selectedDeliveryMethod ? methodTokenAddress(selectedDeliveryMethod) : undefined,
            fundingCurrency: hedgedFunding,
            fundingNetwork: convertFunding?.network,
            fundingToken: convertFunding?.token
          })

      request
        .then(estimate => {
          if (cancelled) return
          setFeeEstimate(estimate)
          setFeeEstimateFor(requested)
        })
        .catch(err => {
          console.error('Failed to estimate send fee', err)
          if (cancelled) return
          setFeeEstimate(null)
          setFeeEstimateFor(undefined)
        })
        .finally(() => {
          if (!cancelled) setIsEstimatingFee(false)
        })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(timeout)
    }
  }, [needsQuote, flow.kind, amount, amountMode, cashFundingCurrency, selectedFundingKey, selectedDeliveryKey])

  useEffect(() => { cashSubmissionKey.current = crypto.randomUUID() }, [amount, selectedFundingKey, destination])

  const bankAccountId = flow.kind === 'bankWithdrawal' ? flow.account.id : undefined
  useEffect(() => {
    setBankEstimate(null)
    setBankQuoteError('')
    if (!bankAccountId || !selectedFundingAsset || !amount || Number(amount) <= 0) {
      setIsQuotingBank(false)
      return
    }
    let cancelled = false
    setIsQuotingBank(true)
    const timeout = window.setTimeout(() => {
      Radom.quoteBankWithdrawal({
        network: selectedFundingAsset.network,
        token: selectedFundingAsset.token,
        amount,
        withdrawalAccountId: bankAccountId
      })
        .then(estimate => { if (!cancelled) setBankEstimate(estimate) })
        .catch(err => { if (!cancelled) setBankQuoteError(err.response?.data?.error || 'Unable to price this bank withdrawal. Try again.') })
        .finally(() => { if (!cancelled) setIsQuotingBank(false) })
    }, 300)
    return () => { cancelled = true; window.clearTimeout(timeout) }
  }, [bankAccountId, amount, selectedFundingKey])

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

  // Same price-quote state the Funding view uses for balance valuation, so
  // crypto sends get a live approximate value line.
  const approxFiatValue = (ticker: string | undefined, assetAmount: string): number | undefined => {
    if (!ticker || !assetAmount || Number(assetAmount) <= 0) return undefined
    const quote = Treasury.priceQuotes.find(p => p.from.toLowerCase() === ticker.toLowerCase())
    if (!quote) return undefined
    return Number(assetAmount) * quote.value
  }

  const estimatedTotalDebit = feeEstimate?.estimatedTotalDebitUsd
  const cashHasInsufficientFunding = isEurRecipient &&
    !!estimatedTotalDebit && !!selectedFundingAsset && !selectedFundingAsset.convert &&
    Number(estimatedTotalDebit) > Number(selectedFundingAsset.maxAmount || 0)
  // Stablecoin-funded crypto payouts are priced at review; until then the
  // balance check uses the live price feed.
  const hedgedFunding = flow.kind === 'cryptoPayout' ? selectedFundingAsset?.fundingCurrency : undefined
  const hedgedEstimatedCost = hedgedFunding && selectedDeliveryMethod
    ? approxFiatValue(selectedDeliveryMethod.ticker, amount)
    : undefined
  const hedgedEntry = hedgedQuote?.cryptoEntries?.[0]?.hedge
  const convertFunding = selectedFundingAsset?.convert ? selectedFundingAsset : undefined
  const convertFundingTicker = convertFunding
    ? getMethod(convertFunding.network, convertFunding.token).ticker
    : undefined
  // The balance a crypto send actually spends when it is not the sent asset:
  // a converted balance, or the stablecoin the asset is bought with.
  const payFundingTicker: string | undefined = convertFundingTicker ?? hedgedFunding
  const convertIntoTicker = isEurRecipient ? 'USDC' : (selectedDeliveryMethod?.ticker ?? '')
  const priceOf = (ticker?: string): number | undefined => {
    if (!ticker) return undefined
    const quote = Treasury.priceQuotes.find(p => p.from.toLowerCase() === ticker.toLowerCase())
    return quote && quote.value > 0 ? quote.value : undefined
  }
  // Whether the fee estimate on hand answers the current request, or at least
  // the current pair. A quote for another amount is shown dimmed until the
  // fresh one lands; it is never combined with the current amount.
  const quoteIsForPair = !!feeEstimate && feeEstimateFor?.pair === feeQuotePair
  const quoteIsCurrent = quoteIsForPair && feeEstimateFor?.amount === amount && feeEstimateFor?.mode === amountMode
  // Before review the conversion is sized off the price feed; the review carries the exchange's own number.
  const convertEstimatedFrom = ((): number | undefined => {
    if (!convertFunding || !amount) return undefined
    if (quoteIsForPair && feeEstimate?.estimatedFundingAmount != null) return Number(feeEstimate.estimatedFundingAmount)
    if (convertQuote) return Number(convertQuote.fromAmount)
    const fundingPrice = priceOf(convertFundingTicker)
    if (!fundingPrice) return undefined
    if (isEurRecipient) {
      // The conversion must produce the USDC the bank payout debits.
      return estimatedTotalDebit ? Number(estimatedTotalDebit) * (priceOf('USDC') ?? 1) / fundingPrice : undefined
    }
    if (!selectedDeliveryMethod) return undefined
    const deliveryPrice = priceOf(selectedDeliveryMethod.ticker)
    return deliveryPrice ? Number(amount) * deliveryPrice / fundingPrice : undefined
  })()
  const convertHasInsufficientFunding = !!convertFunding && convertEstimatedFrom !== undefined &&
    convertEstimatedFrom > Number(convertFunding.maxAmount || 0)
  const hedgedPricedCost = hedgedFunding && quoteIsForPair && feeEstimate?.fundingTicker === hedgedFunding &&
    feeEstimate.estimatedFundingAmount != null
    ? Number(feeEstimate.estimatedFundingAmount)
    : undefined

  // The quoted network fee of a crypto payout is in the delivered asset (the
  // API converts the gas cost into that asset's units; the field is named in
  // USD and only reads as dollars for the dollar stablecoins). It only leaves
  // the balance on a same-asset or converted payout: a stablecoin-funded one
  // pays the exchange's costs instead. It is a property of the delivered
  // asset, so it holds across amounts.
  const cryptoNetworkFee = flow.kind === 'cryptoPayout' && feeEstimate && !hedgedFunding &&
    feeEstimateFor?.delivery === (selectedDeliveryKey ?? '')
    ? Number(feeEstimate.estimatedFeeUsd)
    : undefined
  const fundingKind: FundingKind | undefined = convertFunding ? 'converted' : hedgedFunding ? 'stablecoin' : undefined
  // What the quote for this pair says the funding costs, the recipient amount
  // it was priced for, and the rate it implies with the listed cost taken out.
  const quotedFundingAmount = quoteIsForPair && fundingKind && feeEstimate?.estimatedFundingAmount != null &&
    (feeEstimate.fundingTicker ?? payFundingTicker) === payFundingTicker
    ? Number(feeEstimate.estimatedFundingAmount)
    : undefined
  const quotedConversionCost = quoteIsForPair && feeEstimate?.estimatedConversionCostUsd != null
    ? Number(feeEstimate.estimatedConversionCostUsd)
    : 0
  const quotedPoint: FundingQuotePoint | undefined = quotedFundingAmount !== undefined && feeEstimateFor
    ? { recipient: Number(feeEstimateFor.amount), funding: quotedFundingAmount }
    : undefined
  const quotedRate = fundingKind && quotedPoint
    ? quotedConversionRate(fundingKind, quotedPoint, cryptoNetworkFee ?? 0, quotedConversionCost)
    : undefined
  const hedgedHasInsufficientFunding = !!hedgedFunding && !!selectedFundingAsset && (
    hedgedEntry
      ? Number(hedgedEntry.stablecoinCost) > Number(selectedFundingAsset.maxAmount || 0)
      : hedgedPricedCost !== undefined
        ? hedgedPricedCost > Number(selectedFundingAsset.maxAmount || 0)
        : hedgedEstimatedCost !== undefined && hedgedEstimatedCost > Number(selectedFundingAsset.maxAmount || 0))

  const requiredWritePermission = flow.kind === 'cryptoWithdrawal' ||
    flow.kind === 'ivyCashWithdrawal' ||
    flow.kind === 'bankWithdrawal'
    ? 'withdrawal' as const
    : flow.kind === 'cryptoPayout' || flow.kind === 'fiatPayout'
      ? 'payout' as const
      : undefined

  const blockers = useMemo((): string[] => {
    const list: string[] = []
    if (flow.kind === 'idle') {
      list.push('Choose who to send to first.')
      return list
    }
    if (flow.kind === 'blocked') {
      list.push(flow.reason)
      return list
    }
    if (flow.kind === 'customerPending') {
      list.push(isLoadingExternalAccounts
        ? 'Loading this contact’s saved accounts…'
        : 'Choose where this contact should receive the funds.')
      return list
    }

    if (requiredWritePermission === 'withdrawal' && !canWriteWithdrawal) {
      list.push('Your role can’t create withdrawals. Ask an organization admin for the WriteWithdrawal permission.')
    }
    if (requiredWritePermission === 'payout' && !canWritePayouts) {
      list.push('Your role can’t create payouts. Ask an organization admin for the WritePayouts permission.')
    }

    if (flow.kind === 'cryptoPayout' && !selectedDeliveryMethod) {
      list.push('Choose what the recipient should receive.')
    } else if (eligibleFundingAssets.length === 0) {
      list.push(flow.kind === 'cryptoPayout' && selectedDeliveryMethod
        ? `You don’t hold ${selectedDeliveryMethod.ticker} on ${methodNetworkName(selectedDeliveryMethod)} and no ` +
          'stablecoin balance can buy it. Choose another asset or deposit first.'
        : 'You don’t hold a balance that can fund this transfer. Deposit first from Add funds.')
    } else if (!selectedFundingAsset) {
      list.push('Choose the balance to pay with.')
    }

    if (flow.kind === 'bankWithdrawal' && Number(amount) > 0 && (!bankEstimate || isQuotingBank)) {
      list.push(bankQuoteError || 'Fetching the latest bank withdrawal estimate…')
    }

    if (!amount || Number(amount) <= 0) {
      list.push('Enter an amount greater than zero.')
    } else {
      if ((flow.kind === 'cryptoWithdrawal' || flow.kind === 'cryptoPayout' || flow.kind === 'bankWithdrawal') &&
        selectedFundingAsset && selectedFundingMethod &&
        !selectedFundingAsset.fundingCurrency && !selectedFundingAsset.convert &&
        Number(amount) > Number(selectedFundingAsset.maxAmount)) {
        list.push(`Amount exceeds the available ${selectedFundingMethod.ticker} balance.`)
      }
      if (hedgedHasInsufficientFunding && hedgedFunding) {
        list.push(`Your ${hedgedFunding} balance is lower than this payout costs.`)
      }
      if (convertHasInsufficientFunding && convertFundingTicker) {
        list.push(`Your ${convertFundingTicker} balance is lower than this transfer costs.`)
      }
      if (isEurRecipient && !isEstimatingFee && !feeEstimate) {
        list.push('Waiting for a quote before this can be sent.')
      }
      if (isEstimatingFee) {
        list.push('Fetching the latest quote…')
      }
      if (cashHasInsufficientFunding) {
        list.push(`Available ${cashFundingCurrency} balance is lower than the estimated debit.`)
      }
      if (isEurRecipient && feeEstimate?.estimatedFundingAmountUsd != null) {
        // Undefined means an API without the field (use the default); null means the API
        // says there is no minimum for this payout, e.g. one the EUR float covers.
        const minimumFunding = feeEstimate.minimumFundingUsd === undefined
          ? BANK_PAYOUT_MINIMUM_FUNDING
          : Number(feeEstimate.minimumFundingUsd ?? 0)
        const funding = Number(feeEstimate.estimatedFundingAmountUsd)
        if (funding > 0 && funding < minimumFunding) {
          const smallestEur = Math.ceil(minimumFunding * Number(amount) / funding)
          list.push(amountMode === 'source'
            ? `The smallest bank payout is ${minimumFunding} ${cashFundingCurrency}.`
            : `The smallest bank payout is ${minimumFunding} ${cashFundingCurrency}, about ${smallestEur} EUR.`)
        }
      }
    }

    return list
  }, [
    flow,
    selectedDeliveryMethod,
    hedgedFunding,
    convertHasInsufficientFunding,
    convertFundingTicker,
    requiredWritePermission,
    canWriteWithdrawal,
    canWritePayouts,
    eligibleFundingAssets.length,
    selectedFundingAsset,
    selectedFundingMethod,
    amount,
    isEurRecipient,
    isEstimatingFee,
    feeEstimate,
    bankEstimate,
    bankQuoteError,
    isQuotingBank,
    cashHasInsufficientFunding,
    hedgedHasInsufficientFunding,
    isLoadingExternalAccounts,
    expectedUsdcNetwork,
    cashFundingCurrency,
    amountMode
  ])

  const canSend = blockers.length === 0 && !isSubmitting
  // An empty amount field is the starting state, not a mistake to flag yet.
  const shownBlocker = blockers[0] === 'Enter an amount greater than zero.' && amount.trim() === ''
    ? undefined
    : blockers[0]

  const resetFlow = (): void => {
    setDestination(undefined)
    setAmount('')
    setPendingSave(undefined)
    setAmountMode('recipient')
    setPayWithAmount('')
    setFeeEstimate(null)
    setQuoteExpanded(false)
    setSelectedFundingKey(undefined)
    setSelectedExternalAccountId(undefined)
  }

  // Recent one-tap chips: recent withdrawals map back to saved accounts,
  // recent payout entries map back to customers. Anything unmappable (raw
  // addresses, deleted recipients) is skipped rather than guessed at.
  const recentDestinations = useMemo((): SendDestination[] => {
    interface Candidate { timestamp: number, destination: SendDestination }
    const candidates: Candidate[] = []

    for (const withdrawal of recentWithdrawals) {
      const account = accounts.find(a => a.id === withdrawal.withdrawalAccountId)
      if (account) {
        candidates.push({
          timestamp: new Date(withdrawal.requestedAt).getTime(),
          destination: { kind: 'ownAccount', account }
        })
      }
    }

    for (const payout of recentPayouts) {
      const customerIds = [
        ...(payout.cryptoEntries ?? []).map(entry => entry.customerId),
        ...(payout.fiatEntries ?? []).map(entry => entry.customerId)
      ].filter((id): id is string => !!id)
      for (const customerId of customerIds) {
        const customer = customers.find(c => c.id === customerId)
        if (customer) {
          candidates.push({
            timestamp: new Date(payout.createdAt).getTime(),
            destination: { kind: 'customer', customer }
          })
        }
      }
    }

    const seen = new Set<string>()
    const chips: SendDestination[] = []
    for (const candidate of candidates.sort((a, b) => b.timestamp - a.timestamp)) {
      const key = destinationKey(candidate.destination)
      if (seen.has(key)) continue
      seen.add(key)
      chips.push(candidate.destination)
    }
    return chips.slice(0, 5)
  }, [recentWithdrawals, recentPayouts, accounts, customers])

  // Crypto payouts are labelled in what the recipient gets; every other flow sends the funding asset itself.
  const sentTicker = isCryptoDelivery ? selectedDeliveryMethod?.ticker : selectedFundingMethod?.ticker
  const feeOnTop = !DEDUCT_FEES
  const recipientGetsCrypto = cryptoNetworkFee !== undefined && !feeOnTop
    ? Math.max(Number(amount) - cryptoNetworkFee, 0)
    : Number(amount)
  // What a same-asset crypto payout takes from the balance: the amount plus the fee charged on top.
  const estimatedTotalCrypto = cryptoNetworkFee !== undefined && feeOnTop
    ? Number(amount) + cryptoNetworkFee
    : Number(amount)
  const recipientAmountLabel = flow.kind === 'bankWithdrawal'
    ? `${formatQuoteAmount(bankEstimate ? Number(bankEstimate.recipientAmount) : undefined, 2)} ${flow.currency}`
    : flow.kind === 'ivyCashWithdrawal'
      ? amountMode === 'recipient'
        ? `${formatQuoteAmount(Number(amount) || undefined, 2)} EUR`
        : `${formatQuoteAmount(feeEstimate?.estimatedRecipientAmount, 2)} EUR`
      : flow.kind === 'fiatPayout'
        ? `${formatQuoteAmount(Number(amount) || undefined, 2)} EUR`
        : `${formatAssetAmount(amount ? recipientGetsCrypto : undefined, sentTicker)} ${sentTicker ?? ''}`.trim()
  const payUnit = cashFundingCurrency
  // A converted payout's estimate already covers the network fee; the sizing
  // done here for the review does not, so the estimate is the better total.
  const convertTotalFrom = convertQuote && flow.kind === 'cryptoPayout' && quoteIsCurrent && quotedFundingAmount !== undefined
    ? String(quotedFundingAmount)
    : convertQuote?.fromAmount
  const youPayLabel = flow.kind === 'bankWithdrawal'
    ? `${formatAssetAmount(amount, selectedFundingMethod?.ticker)} ${selectedFundingMethod?.ticker ?? ''}`
    : convertTotalFrom && convertFundingTicker
      ? `≈ ${formatAssetAmount(convertTotalFrom, convertFundingTicker)} ${convertFundingTicker}`
      : isEurRecipient
        ? `${formatQuoteAmount(feeEstimate?.estimatedTotalDebitUsd)} ${payUnit}`
        : hedgedEntry
          ? `${formatAssetAmount(hedgedEntry.stablecoinCost, hedgedEntry.fundingCurrency)} ${hedgedEntry.fundingCurrency}`
          : cryptoNetworkFee !== undefined
            ? `${formatAssetAmount(estimatedTotalCrypto, sentTicker)} ${sentTicker ?? ''}`.trim()
            : recipientAmountLabel
  // Exact where the figure is what the request debits (a bank withdrawal's
  // funding amount, a locked stablecoin cost, a withdrawal's own amount);
  // an estimate wherever a quote or a fee estimate is involved.
  const totalLabel = flow.kind === 'bankWithdrawal' || hedgedEntry || (flow.kind === 'cryptoWithdrawal' && !convertTotalFrom)
    ? 'Total debit'
    : 'Estimated total'

  // -------------------------------------------------------------------------
  // Amount card: the "You pay" row and the "Recipient gets" row.
  // -------------------------------------------------------------------------

  // The asset each row shows. Fiat rows borrow the registry's currency entry for its logo.
  const payMethod = selectedFundingMethod
  const recipientMethod: ManagedPaymentMethod | undefined = flow.kind === 'bankWithdrawal'
    ? getMethod('Fiat', flow.currency)
    : isEurRecipient ? getMethod('Fiat', 'EUR') : selectedDeliveryMethod
  // Two rows whenever what leaves the balance is not what the recipient gets.
  const hasTwoRows = flow.kind === 'bankWithdrawal' ||
    (isEurRecipient && !!selectedFundingMethod) ||
    (isCryptoDelivery && !!payFundingTicker)
  // Which row is typed into. A bank withdrawal only takes the funding amount.
  // A EUR withdrawal switches sides through amountMode, which the API
  // understands. A converted crypto send keeps the recipient amount as the
  // request value and sizes it from what was typed on the pay side.
  const typedSide: 'pay' | 'recipient' = flow.kind === 'bankWithdrawal'
    ? 'pay'
    : hasTwoRows && amountMode === 'source' ? 'pay' : 'recipient'
  const cryptoPaySideTyped = isCryptoDelivery && typedSide === 'pay'
  // A EUR withdrawal paid from a converted balance types the pay row in that
  // balance's asset. The request stays the USDC source amount the API sizes
  // the payout from, and that amount is sized from what was typed the same
  // way a converted crypto send sizes its recipient amount.
  const cashPaySideTyped = flow.kind === 'ivyCashWithdrawal' && typedSide === 'pay' && !!convertFunding
  const paySideTyped = cryptoPaySideTyped || cashPaySideTyped
  // The unit of the request value that the typed pay amount is sized into.
  const sizedTicker = cashPaySideTyped ? 'USDC' : sentTicker
  const canSwitchSide = (flow.kind === 'ivyCashWithdrawal' &&
    (!convertFunding || (!!convertFundingTicker && !!priceOf(convertFundingTicker) && !!priceOf('USDC')))) ||
    (isCryptoDelivery && !!payFundingTicker && !!sentTicker && !!priceOf(payFundingTicker) && !!priceOf(sentTicker))

  // What the pay row shows while the recipient amount is typed: the last
  // quote for this pair (dimmed by the row while a fresh one loads), else a
  // price-feed estimate until one lands.
  const hasAmount = !!amount && Number(amount) > 0
  const payCounterAmount: number | undefined = !hasAmount
    ? undefined
    : convertFunding
      ? convertEstimatedFrom
      : isEurRecipient
        ? (quoteIsForPair && feeEstimate?.estimatedTotalDebitUsd != null
          ? Number(feeEstimate.estimatedTotalDebitUsd)
          : undefined)
        : hedgedFunding ? (hedgedPricedCost ?? hedgedEstimatedCost) : undefined
  const recipientCounterAmount: number | undefined = flow.kind === 'bankWithdrawal'
    ? (bankEstimate ? Number(bankEstimate.recipientAmount) : undefined)
    : flow.kind === 'ivyCashWithdrawal' && amountMode === 'source'
      ? (quoteIsForPair && feeEstimate && hasAmount ? Number(feeEstimate.estimatedRecipientAmount) : undefined)
      : cryptoPaySideTyped && hasAmount ? Number(amount) : undefined
  const payCounterPending = needsQuote && isEstimatingFee
  const recipientCounterPending = flow.kind === 'bankWithdrawal'
    ? isQuotingBank
    : needsQuote && isEstimatingFee &&
      (paySideTyped || (flow.kind === 'ivyCashWithdrawal' && amountMode === 'source'))
  // The market rate from the price feed, delivered units per funding unit.
  const marketRate = payFundingTicker && sizedTicker && priceOf(payFundingTicker) && priceOf(sizedTicker)
    ? (priceOf(payFundingTicker) as number) / (priceOf(sizedTicker) as number)
    : undefined

  // Sizes the recipient amount from what was typed on the pay side: the
  // fee-exclusive rate of the last quote for this pair, with its fixed cost
  // taken out first; until a quote has priced the pair, the price feed less
  // a little for the conversion's own cost.
  const deliveryAmountFor = (typedPay: string): string => {
    const pay = Number(typedPay)
    if (!(pay > 0) || !fundingKind || !sizedTicker) return ''
    const rate = quotedRate ?? (marketRate !== undefined ? marketRate * 0.985 : undefined)
    if (!rate) return ''
    const recipient = recipientForFunding(
      fundingKind, pay, rate, cryptoNetworkFee ?? 0, quotedRate !== undefined ? quotedConversionCost : 0)
    return recipient > 0 ? floorAssetAmount(sizedTicker, recipient.toFixed(8)) : ''
  }
  const typedAmountValue = paySideTyped ? payWithAmount : amount
  // Pay-side typing: the quotes answered for the sized recipient amounts, so
  // the next request can land its funding on what was typed.
  const reconciliation = useRef<{ typed: string, points: FundingQuotePoint[], steps: number }>({
    typed: '', points: [], steps: 0
  })
  const handleAmountTyped = (raw: string): void => {
    if (!/^\d*\.?\d*$/.test(raw)) return
    if (paySideTyped) {
      reconciliation.current = { typed: raw, points: [], steps: 0 }
      setPayWithAmount(raw)
      setAmount(deliveryAmountFor(raw))
      return
    }
    setAmount(raw)
  }
  // Moves the caret to the other row. The counter-amount shown there becomes
  // the typed value so the pair stays put; with nothing quoted yet both clear.
  const switchTypedSide = (): void => {
    if (!canSwitchSide) return
    const next = typedSide === 'pay' ? 'recipient' : 'pay'
    pendingAmountFocus.current = true
    if (isEurRecipient && convertFunding) {
      // The pay row is typed in the converted asset; the request value is the
      // USDC the bank payout debits (source) or the EUR it delivers (recipient).
      if (next === 'pay') {
        const seeded = payCounterAmount !== undefined ? plainAmount(payCounterAmount, convertFundingTicker) : ''
        reconciliation.current = { typed: seeded, points: [], steps: 0 }
        setPayWithAmount(seeded)
        setAmount(quoteIsForPair && feeEstimate?.estimatedTotalDebitUsd != null
          ? plainAmount(Number(feeEstimate.estimatedTotalDebitUsd), 'USDC')
          : '')
      } else {
        setAmount(recipientCounterAmount !== undefined ? plainAmount(recipientCounterAmount, undefined, true) : '')
      }
    } else if (isEurRecipient) {
      // The request value changes unit (EUR or the funding stablecoin).
      const carried = next === 'pay' ? payCounterAmount : recipientCounterAmount
      setAmount(carried !== undefined
        ? plainAmount(carried, selectedFundingMethod?.ticker, next === 'recipient')
        : '')
    } else if (next === 'pay') {
      const seeded = payCounterAmount !== undefined ? plainAmount(payCounterAmount, payFundingTicker) : ''
      reconciliation.current = { typed: seeded, points: [], steps: 0 }
      setPayWithAmount(seeded)
      if (payCounterAmount === undefined) setAmount('')
    }
    setAmountMode(next === 'pay' ? 'source' : 'recipient')
  }

  // Once the quote for a recipient amount sized from the pay side lands, the
  // recipient amount is nudged until the quoted funding matches what was
  // typed, within rounding and in a couple of steps at most. Only recipient
  // amounts are ever requested, so the recipient amount is the exact figure
  // and the funding stays the estimate.
  useEffect(() => {
    if (!paySideTyped || !quoteIsCurrent || !fundingKind || !quotedPoint || !sizedTicker) return
    const pay = Number(payWithAmount)
    if (!(pay > 0)) return
    if (reconciliation.current.typed !== payWithAmount) {
      reconciliation.current = { typed: payWithAmount, points: [], steps: 0 }
    }
    const state = reconciliation.current
    if (state.points.some(point => point.recipient === quotedPoint.recipient)) return
    state.points = [...state.points.slice(-1), quotedPoint]
    if (fundingMatchesTyped(pay, quotedPoint.funding) || state.steps >= 3) return
    const rate = quotedRate ?? marketRate
    if (!rate) return
    const next = reconcileRecipientForFunding(pay, state.points, rate)
    if (next === undefined) return
    const nextAmount = next > 0 ? floorAssetAmount(sizedTicker, next.toFixed(8)) : ''
    if (nextAmount === amount) return
    state.steps += 1
    setAmount(nextAmount)
  }, [quoteIsCurrent, paySideTyped, payWithAmount])

  const maxTypedValue = selectedFundingAsset && selectedFundingMethod
    ? floorAssetAmount(selectedFundingMethod.ticker, selectedFundingAsset.maxAmount)
    : undefined
  // Max fills the typed row from the balance that pays for it, so only where
  // that row is the paying asset (or sized from it).
  const showMax = !!maxTypedValue && (
    (isCryptoDelivery && (!payFundingTicker || typedSide === 'pay')) ||
    flow.kind === 'bankWithdrawal' ||
    (flow.kind === 'ivyCashWithdrawal' && amountMode === 'source'))
  const balanceExceeded = !!selectedFundingAsset && hasAmount && (
    hedgedFunding
      ? hedgedHasInsufficientFunding
      : convertFunding
        ? convertHasInsufficientFunding ||
          (cashPaySideTyped && Number(payWithAmount) > Number(convertFunding.maxAmount || 0))
        : isEurRecipient
          ? cashHasInsufficientFunding
          : Number(amount) > Number(selectedFundingAsset.maxAmount || 0))

  // A side that can no longer be typed (a converted balance was picked for a
  // EUR withdrawal, or a crypto pair lost its price feed) falls back to the
  // recipient amount.
  useEffect(() => {
    if (amountMode !== 'source' || canSwitchSide) return
    setAmountMode('recipient')
    setPayWithAmount('')
    if (isEurRecipient) setAmount('')
  }, [amountMode, canSwitchSide])

  /** Muted account detail for the review card and success summary. */
  const selectedDestinationDetail = (): string => {
    if (!destination) return ''
    switch (destination.kind) {
    case 'ownAccount':
      return withdrawalAccountSubtitle(destination.account)
    case 'customer':
      return selectedExternalAccount
        ? externalAccountLabel(selectedExternalAccount)
        : destination.customer.email?.trim() || 'Saved contact'
    case 'oneOff':
      return destination.value
    }
  }

  const cryptoPayoutCustomerType = (flow: Extract<SendFlow, { kind: 'cryptoPayout' }>): 'Business' | 'Individual' | undefined =>
    flow.customer?.accountType === 'Business' || flow.customer?.accountType === 'Individual'
      ? flow.customer.accountType
      : undefined
  const cryptoPayoutAccountType = (flow: Extract<SendFlow, { kind: 'cryptoPayout' }>): string | undefined =>
    flow.externalAccount?.accountOwnerType || cryptoPayoutCustomerType(flow) || undefined
  // The balance the API converts when the payout is confirmed: a converted
  // choice, or a held asset bound for a network the plain payout cannot deliver
  // on (the API routes that through the exchange).
  const payoutFundingAssetFor = (delivery?: ManagedPaymentMethod): { network: string, token?: string } | undefined => {
    // A private treasury balance is not a balance the API can convert; the
    // plain payout keeps spending it as it always has.
    if (!selectedFundingAsset || selectedFundingAsset.fundingCurrency) return undefined
    if (selectedFundingAsset.treasuryId) return undefined
    const needsRouting = !!delivery && !PLAIN_PAYOUT_NETWORKS.has(methodNetworkName(delivery))
    if (!selectedFundingAsset.convert && !needsRouting) return undefined
    return { network: selectedFundingAsset.network, token: selectedFundingAsset.token }
  }

  const cryptoPayoutEntry = (
    flow: Extract<SendFlow, { kind: 'cryptoPayout' }>,
    delivery: ManagedPaymentMethod
  ): CreateCryptoPayoutEntity => ({
    gateway: 'crypto',
    amount,
    payoutAddress: flow.payoutAddress,
    network: methodNetworkName(delivery),
    token: methodTokenAddress(delivery),
    fundingAsset: payoutFundingAssetFor(delivery),
    customerId: flow.customer?.id,
    accountType: cryptoPayoutCustomerType(flow),
    email: flow.customer?.email || undefined,
    name: flow.customer ? (flow.customer.name || flow.customer.companyName || undefined) : undefined
  })

  // The API may defer an auto-confirm (a recipient under review, or a rule the
  // create call does not check). A payout still awaiting confirmation has not
  // gone out, so confirm it here and let the reason surface as an error.
  const settleAutoConfirmed = async (
    created: { id: string, status: string },
    base: Omit<SuccessInfo, 'heading' | 'note'>
  ): Promise<SuccessInfo> => {
    let status = created.status
    if (status === 'AwaitingConfirmation') {
      await Radom.confirmPayout(created.id)
      try {
        status = (await Radom.getPayout(created.id)).status
      } catch {
        // The confirm went through; the history shows where it is.
      }
    }
    if (status === 'Converting') {
      return {
        ...base,
        heading: 'Converting, then sending',
        note: 'Your funding balance is being converted first. The payout goes out as soon as the conversion lands, usually within a minute. Track it from your payout history.'
      }
    }
    return status === 'InReview'
      ? { ...base, heading: 'Submitted for review', note: 'This payout is being checked before it goes out. Track it from your payout history.' }
      : { ...base, heading: 'Sent', note: 'Payout created. Track it from your payout history.' }
  }

  const submitSend = async (): Promise<void> => {
    if (!canSend) return

    const recipientName = destination ? destinationDisplayName(destination) : 'recipient'
    const recipientDetail = selectedDestinationDetail()
    const sentAmountLabel = recipientAmountLabel

    setIsSubmitting(true)
    try {
      let outcome: SuccessInfo | undefined
      // Convert-then-send: the swap credits the delivered asset first; if it lands
      // later (large conversions settle before crediting), the send waits for you.
      if (flow.kind === 'cryptoWithdrawal' && convertFunding && convertQuote && selectedDeliveryMethod && convertFundingTicker) {
        await Radom.initiateSwap({
          from: { network: convertFunding.network, token: convertFunding.token },
          into: {
            network: methodNetworkName(selectedDeliveryMethod),
            token: methodTokenAddress(selectedDeliveryMethod)
          },
          amount: Number(convertQuote.fromAmount)
        })
        setConvertQuote(null)
        let landed = false
        for (let attempt = 0; attempt < 8 && !landed; attempt += 1) {
          await Treasury.loadBalances()
          landed = deliveryBalanceNow(selectedDeliveryMethod) >= Number(amount)
          if (!landed) await new Promise(resolve => setTimeout(resolve, 1500))
        }
        if (!landed) {
          setSuccessInfo({
            heading: 'Conversion started',
            amountLabel: sentAmountLabel,
            recipientName,
            recipientDetail,
            note: `Your ${convertFundingTicker} is being converted. Once the ${selectedDeliveryMethod.ticker} lands in your balance, send it from there.`,
            historyHref: '/transactions',
            historyLabel: 'View transactions'
          })
          setStep('success')
          resetFlow()
          return
        }
      }
      if (flow.kind === 'cryptoWithdrawal' || flow.kind === 'bankWithdrawal') {
        if (!selectedFundingAsset) return
        const sending = flow.kind === 'cryptoWithdrawal' && selectedDeliveryMethod
          ? { network: methodNetworkName(selectedDeliveryMethod), token: methodTokenAddress(selectedDeliveryMethod) }
          : { network: selectedFundingAsset.network, token: selectedFundingAsset.token }
        await Radom.submitWithdrawalRequest(
          sending.network,
          amount,
          flow.account.id,
          false,
          sending.token,
          selectedFundingAsset.convert ? undefined : selectedFundingAsset.treasuryId,
          cashSubmissionKey.current
        )
        cashSubmissionKey.current = crypto.randomUUID()
        outcome = {
          heading: 'Request submitted',
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          note: 'Withdrawal request submitted. Follow its progress in transactions.',
          historyHref: '/transactions',
          historyLabel: 'View transactions'
        }
      } else if (flow.kind === 'ivyCashWithdrawal') {
        const user = await User.getUser()
        const accountOwnerType = flow.account.accountDetails.Fiat?.accountOwnerType === 'Individual'
          ? 'Individual'
          : 'Business'
        const createdPayout = await Radom.createPayout({
          idempotencyKey: cashSubmissionKey.current,
          payouts: [{
            gateway: 'fiatWithdrawalAccount',
            amount,
            amountMode,
            withdrawalAccountId: flow.account.id,
            accountType: accountOwnerType,
            fundingCurrency: cashFundingCurrency,
            fundingAsset: payoutFundingAssetFor(),
            email: user.emailAddress || '',
            name: flow.account.accountDetails.Fiat?.accountOwnerName
          }],
          accountType: accountOwnerType,
          isTestMode: testMode,
          deductFees: DEDUCT_FEES,
          sendEmailReceipts: false
        })
        await Radom.confirmPayout(createdPayout.id)
        cashSubmissionKey.current = crypto.randomUUID()
        outcome = {
          heading: 'Sent',
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          note: 'Bank withdrawal submitted. Track it from your payout history.',
          historyHref: ROUTES.PAYOUTS,
          historyLabel: 'View history'
        }
      } else if (flow.kind === 'fiatPayout') {
        const accountType = flow.externalAccount.accountOwnerType || flow.customer.accountType || undefined
        const createdPayout = await Radom.createPayout({
          payouts: [{
            gateway: 'fiat',
            amount,
            externalAccountId: flow.externalAccount.id,
            fundingCurrency: cashFundingCurrency,
            fundingAsset: payoutFundingAssetFor(),
            email: flow.customer.email || '',
            name: flow.customer.name || flow.customer.companyName || undefined
          }],
          accountType,
          isTestMode: testMode,
          deductFees: DEDUCT_FEES,
          sendEmailReceipts: false,
          autoConfirm: true,
          idempotencyKey: cashSubmissionKey.current
        })
        cashSubmissionKey.current = crypto.randomUUID()
        outcome = await settleAutoConfirmed(createdPayout, {
          amountLabel: sentAmountLabel,
          recipientName,
          recipientDetail,
          historyHref: ROUTES.PAYOUTS,
          historyLabel: 'View history'
        })
      } else if (flow.kind === 'cryptoPayout') {
        if (!selectedFundingAsset || !selectedDeliveryMethod) return
        if (selectedFundingAsset.fundingCurrency) {
          if (!hedgedQuote) return
          await Radom.confirmPayout(hedgedQuote.id)
          hedgedQuoteRef.current = null
          setHedgedQuote(null)
          outcome = {
            heading: 'Sent',
            amountLabel: sentAmountLabel,
            recipientName,
            recipientDetail,
            note: 'Payout created. Track it from your payout history.',
            historyHref: ROUTES.PAYOUTS,
            historyLabel: 'View history'
          }
        } else {
          const createdPayout = await Radom.createPayout({
            payouts: [cryptoPayoutEntry(flow, selectedDeliveryMethod)],
            accountType: cryptoPayoutAccountType(flow),
            isTestMode: testMode,
            deductFees: DEDUCT_FEES,
            sendEmailReceipts: false,
            autoConfirm: true,
            idempotencyKey: cashSubmissionKey.current
          })
          cashSubmissionKey.current = crypto.randomUUID()
          outcome = await settleAutoConfirmed(createdPayout, {
            amountLabel: sentAmountLabel,
            recipientName,
            recipientDetail,
            historyHref: ROUTES.PAYOUTS,
            historyLabel: 'View history'
          })
        }
      } else {
        return
      }

      setSuccessInfo(outcome)
      setStep('success')
      resetFlow()
      void Treasury.loadBalances()
      void loadActivity()
    } catch (err: any) {
      console.error('Failed to send', err)
      errorToast(err?.response?.data?.error || err?.message || 'Failed to send')
    } finally {
      setIsSubmitting(false)
    }
  }

  const deliveryBalanceNow = (method: ManagedPaymentMethod): number => {
    const network = methodNetworkName(method)
    return [...Treasury.managedBalances, ...Treasury.privateManagedBalances]
      .filter(b => b.asset
        ? b.asset.toUpperCase() === method.ticker.toUpperCase()
        : b.network === network && getMethod(b.network, b.token).ticker === method.ticker)
      .reduce((sum, b) => sum + Number(b.balance), 0)
  }

  // Sizes the conversion so the delivered amount covers what goes out: one
  // quote to learn the rate, a second at the sized amount with a 1% buffer.
  const priceConversion = async (
    funding: FundingAsset,
    delivery: ManagedPaymentMethod,
    target: number
  ): Promise<{ fromAmount: string, quotedAmount: string }> => {
    const from = { network: funding.network, token: funding.token }
    const into = { network: methodNetworkName(delivery), token: methodTokenAddress(delivery) }
    const fundingTicker = getMethod(funding.network, funding.token).ticker
    const guess = (priceOf(delivery.ticker) && priceOf(fundingTicker))
      ? target * (priceOf(delivery.ticker) as number) / (priceOf(fundingTicker) as number)
      : target
    const first = await Radom.getSwapRates({ from, into, amount: Number(guess.toPrecision(8)) })
    const firstOut = Number(first.amount)
    if (!(firstOut > 0)) throw new Error('The exchange could not price this conversion')
    let fromAmount = guess * target / firstOut * 1.01
    let sized = await Radom.getSwapRates({ from, into, amount: Number(fromAmount.toPrecision(8)) })
    if (Number(sized.amount) < target) {
      fromAmount = fromAmount * target / Number(sized.amount) * 1.005
      sized = await Radom.getSwapRates({ from, into, amount: Number(fromAmount.toPrecision(8)) })
    }
    return { fromAmount: Number(fromAmount.toPrecision(8)).toString(), quotedAmount: String(sized.amount) }
  }

  const goToReview = async (): Promise<void> => {
    if (blockers.length > 0 || isQuotingHedged) return
    // A converted balance is priced for the review: into the delivered asset,
    // or into the USDC a bank payout debits.
    const convertInto = isEurRecipient
      ? getPaymentMethods().find(m => methodNetworkName(m) === 'Ethereum' && m.ticker === 'USDC')
      : selectedDeliveryMethod
    const convertTarget = isEurRecipient ? Number(feeEstimate?.estimatedTotalDebitUsd ?? 0) : Number(amount)
    if (convertFunding && convertInto && convertTarget > 0) {
      setIsQuotingHedged(true)
      try {
        setConvertQuote(await priceConversion(convertFunding, convertInto, convertTarget))
      } catch (err) {
        errorToast(err.response?.data?.error || err.message)
        return
      } finally {
        setIsQuotingHedged(false)
      }
    }
    if (flow.kind === 'cryptoPayout' && selectedFundingAsset?.fundingCurrency && selectedDeliveryMethod) {
      // The quote is the payout itself, created unconfirmed; Send confirms it.
      // A quote from an earlier visit to the review is cancelled first.
      discardHedgedQuote()
      setIsQuotingHedged(true)
      try {
        const created = await Radom.createPayout({
          payouts: [{
            ...cryptoPayoutEntry(flow, selectedDeliveryMethod),
            fundingCurrency: selectedFundingAsset.fundingCurrency
          }],
          accountType: cryptoPayoutAccountType(flow),
          isTestMode: testMode,
          deductFees: DEDUCT_FEES,
          sendEmailReceipts: false
        })
        hedgedQuoteRef.current = created
        setHedgedQuote(created)
      } catch (err) {
        errorToast(err.response?.data?.error || err.message)
        return
      } finally {
        setIsQuotingHedged(false)
      }
    }
    setStep('review')
  }

  const renderFundingAssetOption = (asset?: FundingAsset): ReactElement => {
    if (!asset) {
      return <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13 }}>Select balance</span>
    }

    const method = getMethod(asset.network, asset.token)
    const balanceLabel = `Available ${formatAssetAmount(floorAssetAmount(method.ticker, asset.maxAmount), method.ticker)} ${method.ticker}`
    const conversion = (asset.fundingCurrency || asset.convert) && selectedDeliveryMethod
      ? `Converted to ${selectedDeliveryMethod.ticker} when you send · `
      : ''
    // Balances are named by asset. The rail they settle on is the system's business
    // and only appears on the confirmation screen for organizations that hold per-rail balances.
    const label = method.ticker
    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SimpleMethodIcon $logo={method.logo} $size={20} />
      <SelectionMeta>
        <SelectionLabel>{label}</SelectionLabel>
        <SelectionSubtext>
          {asset.treasuryId ? `Private treasury · ${balanceLabel}` : `${conversion}${balanceLabel}`}
        </SelectionSubtext>
      </SelectionMeta>
    </div>
  }

  const renderDeliveryOption = (method?: ManagedPaymentMethod): ReactElement => {
    if (!method) {
      return <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13 }}>Select asset</span>
    }
    const held = ownBalanceOf(method)
    const hedgeable = hedgeCurrenciesFor(method)
    const subtext = held > 0
      ? `You hold ${formatAssetAmount(floorAssetAmount(method.ticker, String(held)), method.ticker)} ${method.ticker}`
      : hedgeable.length > 0
        ? `Bought with ${hedgeable.join(' or ')} when you send`
        : canConvert
          ? 'Converted from another balance when you send'
          : 'No balance to send from'
    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SimpleMethodIcon $logo={method.logo} $size={20} />
      <SelectionMeta>
        <SelectionLabel>{deliveryNetworkIsFixed ? method.ticker : `${method.ticker} on ${methodNetworkName(method)}`}</SelectionLabel>
        <SelectionSubtext>{subtext}</SelectionSubtext>
      </SelectionMeta>
    </div>
  }

  const renderFundingAssetList = (closeDropdown: () => void): ReactElement => {
    const query = assetSearch.trim().toLowerCase()
    const searched = eligibleFundingAssets.filter(asset => {
      if (!query) return true
      const method = getMethod(asset.network, asset.token)
      return [method.ticker, method.name, method.currencyName, method.hostChain?.name, asset.network]
        .some(term => term?.toLowerCase().includes(query))
    })

    return <div style={{ minWidth: 320 }}>
      {eligibleFundingAssets.length > 6 && <DropdownSearchContainer>
        <DropdownTextInput
          value={assetSearch}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          onChange={e => setAssetSearch(e.target.value)}
          placeholder='Search balances'
          style={{
            border: '1px solid var(--border-strong, #d8dde7)',
            borderRadius: 10,
            boxSizing: 'border-box',
            minHeight: 38,
            padding: '0 10px'
          }}
          autoFocus />
      </DropdownSearchContainer>}
      {searched.map(asset => <DropdownItem
        key={fundingAssetKey(asset)}
        style={{ padding: '11px 12px' }}
        onClick={() => {
          setSelectedFundingKey(fundingAssetKey(asset))
          closeDropdown()
        }}>
        {renderFundingAssetOption(asset)}
      </DropdownItem>)}
      {searched.length === 0 && <div style={{
        color: 'var(--text-soft, #7d8492)',
        fontSize: 12.5,
        lineHeight: 1.45,
        padding: '8px 12px 14px'
      }}>
        No balances match this search.
      </div>}
      {targetsPending && <div style={{
        color: 'var(--text-soft, #7d8492)',
        fontSize: 12.5,
        lineHeight: 1.45,
        padding: '8px 12px 14px'
      }}>
        Checking which other balances can be converted…
      </div>}
    </div>
  }

  /** "Pay with USDC": the balance choice on a single-row card, where no pay row carries it. */
  const renderFundingTextPicker = (): ReactElement | null => {
    if (eligibleFundingAssets.length <= 1 || !selectedFundingMethod) return null
    return <div onClick={e => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <EscapableDropdown
        iconTrigger
        outerStyle={fundingTextTriggerStyle}
        onClose={() => setAssetSearch('')}
        selectedContent={<span style={{ color: 'var(--text-soft, #7d8492)', fontSize: 12.5, whiteSpace: 'nowrap' }}>
          Pay with <span style={{ color: 'var(--ink, #20242d)', fontWeight: 500 }}>{selectedFundingMethod.ticker}</span>
        </span>}
        dropdownContent={
          <DropdownContext.Consumer>
            {({ closeDropdown }) => renderFundingAssetList(closeDropdown)}
          </DropdownContext.Consumer>
        } />
    </div>
  }

  /** The asset on the left of a row: a picker where there is a choice, a plain chip otherwise. */
  const renderAssetChip = (side: 'pay' | 'recipient'): ReactElement => {
    const method = side === 'pay' ? payMethod : recipientMethod
    // The network matters for what the recipient gets, and for a paying
    // balance only where the organization holds balances per network.
    const network = method && !isFiatMethod(method) && (side === 'recipient' || !unifiedTreasury)
      ? side === 'pay' && selectedFundingAsset?.treasuryId ? 'Private treasury' : methodNetworkName(method)
      : undefined
    const content = <>
      {method?.logo && <SimpleMethodIcon $logo={method.logo} $size={24} />}
      <AssetChipText>
        <AssetChipTicker>{method?.ticker ?? (side === 'pay' ? 'Select balance' : 'Select asset')}</AssetChipTicker>
        {network && <AssetChipNetwork>{network}</AssetChipNetwork>}
      </AssetChipText>
    </>
    const pickable = side === 'pay'
      ? eligibleFundingAssets.length > 1
      : isCryptoDelivery && deliveryChoices.length > 1
    if (!pickable) return <AssetChip>{content}</AssetChip>
    return <div onClick={e => e.stopPropagation()} style={{ flex: '0 0 auto' }}>
      <EscapableDropdown
        iconTrigger
        outerStyle={assetChipStyle}
        onClose={side === 'pay' ? () => setAssetSearch('') : undefined}
        selectedContent={content}
        dropdownContent={
          <DropdownContext.Consumer>
            {({ closeDropdown }) => side === 'pay'
              ? renderFundingAssetList(closeDropdown)
              : <div style={{ minWidth: 300 }}>
                {deliveryChoices.map(choice => <DropdownItem
                  key={deliveryKey(choice)}
                  style={{ padding: '11px 12px' }}
                  onClick={() => {
                    setSelectedDeliveryKey(deliveryKey(choice))
                    setSelectedFundingKey(undefined)
                    closeDropdown()
                  }}>
                  {renderDeliveryOption(choice)}
                </DropdownItem>)}
              </div>}
          </DropdownContext.Consumer>
        } />
    </div>
  }

  const renderDeliveryOptionText = (account?: ExternalAccount): ReactElement => {
    if (!account) {
      return <span style={{ color: 'var(--icon-muted, #747894)', fontSize: 13 }}>Select account</span>
    }

    return <div style={{ alignItems: 'center', display: 'flex', gap: 10, minWidth: 0, width: '100%' }}>
      <SelectionMeta>
        <SelectionLabel>{account.accountOwnerName || account.businessName || 'Saved account'}</SelectionLabel>
        <SelectionSubtext>{externalAccountLabel(account)}</SelectionSubtext>
      </SelectionMeta>
      <div style={{ flexGrow: 1 }} />
      <InlinePill>{account.crypto ? 'Wallet' : 'Bank'}</InlinePill>
    </div>
  }

  const renderDeliverySelect = (): ReactElement | null => {
    if (destination?.kind !== 'customer') return null

    return <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <SectionLabel>Deliver to</SectionLabel>
      {
        isLoadingExternalAccounts
          ? <LoadingBar style={{ borderRadius: 25, width: '100%', height: 44 }} />
          : deliveryOptions.length > 0
            ? <EscapableDropdown
              matchTriggerWidth
              outerStyle={{ minHeight: 44 }}
              selectedContent={renderDeliveryOptionText(selectedExternalAccount)}
              dropdownContent={
                <DropdownContext.Consumer>
                  {({ closeDropdown }) => <div>
                    {deliveryOptions.map(account => <DropdownItem
                      key={account.id}
                      style={{ padding: '11px 12px' }}
                      onClick={() => {
                        setSelectedExternalAccountId(account.id)
                        setSelectedFundingKey(undefined)
                        setAmount('')
                        pendingAmountFocus.current = true
                        closeDropdown()
                      }}>
                      {renderDeliveryOptionText(account)}
                    </DropdownItem>)}
                  </div>}
                </DropdownContext.Consumer>
              } />
            : <EmptyState style={{ minHeight: 84 }}>
              This contact has no saved bank account or wallet yet. Add one from their page under Contacts.
            </EmptyState>
      }
    </div>
  }

  const renderQuoteDetails = (): ReactElement | null => {
    if (flow.kind === 'bankWithdrawal') {
      if (!bankEstimate) {
        // A rejected quote is the bank's or our own validation talking; show
        // it here instead of leaving it to compete with the other blockers.
        return bankQuoteError ? <QuoteBox $error>{bankQuoteError}</QuoteBox> : null
      }
      return <QuoteBox>
        <QuoteRow>
          <QuoteLabel>Estimated recipient amount</QuoteLabel>
          <QuoteValue>
            {formatQuoteAmount(Number(bankEstimate.recipientAmount), 2)} {bankEstimate.destinationCurrency}
          </QuoteValue>
        </QuoteRow>
        <QuoteRow>
          <QuoteLabel>Platform fee</QuoteLabel>
          <QuoteValue>
            {formatAssetAmount(bankEstimate.platformFee, bankEstimate.sourceCurrency)} {bankEstimate.sourceCurrency}
          </QuoteValue>
        </QuoteRow>
        <QuoteRow>
          <QuoteLabel>Estimated bank processing fee</QuoteLabel>
          <QuoteValue>
            {formatAssetAmount(bankEstimate.providerFee, bankEstimate.sourceCurrency)} {bankEstimate.sourceCurrency}
          </QuoteValue>
        </QuoteRow>
        <span>
          {bankEstimate.indicative && 'Exchange rates may change before settlement. '}
          The final recipient amount appears in Transactions.
        </span>
      </QuoteBox>
    }
    if (!feeEstimate) return null

    const hasFundingError = cashHasInsufficientFunding || hedgedHasInsufficientFunding || convertHasInsufficientFunding
    return <QuoteBox $error={hasFundingError}>
      {isEurRecipient
        ? <>
          <QuoteRow>
            <QuoteLabel>Recipient gets</QuoteLabel>
            <QuoteValue>
              {formatQuoteAmount(
                amountMode === 'source' && flow.kind === 'ivyCashWithdrawal'
                  ? feeEstimate.estimatedRecipientAmount
                  : Number(amount),
                2
              )} EUR
            </QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>You pay</QuoteLabel>
            <QuoteValue>
              {convertFunding && convertFundingTicker && feeEstimate.estimatedFundingAmount != null
                ? `≈ ${formatAssetAmount(String(feeEstimate.estimatedFundingAmount), convertFundingTicker)} ${convertFundingTicker} (${formatQuoteAmount(feeEstimate.estimatedTotalDebitUsd)} ${payUnit} converted)`
                : `${formatQuoteAmount(feeEstimate.estimatedTotalDebitUsd)} ${payUnit}`}
            </QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Fee</QuoteLabel>
            <QuoteValue>{formatQuoteAmount(feeEstimate.estimatedFeeUsd)} {payUnit}</QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Rate</QuoteLabel>
            <QuoteValue>1 {payUnit} ≈ {formatQuoteAmount(feeEstimate.exchangeRate, 8)} EUR</QuoteValue>
          </QuoteRow>
        </>
        : <>
          <QuoteRow>
            <QuoteLabel>Recipient gets</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(recipientGetsCrypto, sentTicker)} {sentTicker}
            </QuoteValue>
          </QuoteRow>
          {cryptoNetworkFee !== undefined && <QuoteRow>
            <QuoteLabel>Estimated network fee</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(cryptoNetworkFee, sentTicker)} {sentTicker}
            </QuoteValue>
          </QuoteRow>}
          {payFundingTicker && quotedFundingAmount !== undefined && <QuoteRow>
            <QuoteLabel>You pay</QuoteLabel>
            <QuoteValue>
              ≈ {formatAssetAmount(quotedFundingAmount, payFundingTicker)} {payFundingTicker}
              {quotedConversionCost > 0 &&
                <span style={{ color: 'var(--text-faint, #8a919e)' }}>
                  {' '}(incl. about {formatQuoteAmount(quotedConversionCost)} USD of conversion cost)
                </span>}
            </QuoteValue>
          </QuoteRow>}
          {!payFundingTicker && <QuoteRow>
            <QuoteLabel>{feeOnTop ? 'Estimated total' : 'Total debit'}</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(feeOnTop ? estimatedTotalCrypto : Number(amount), sentTicker)} {sentTicker}
            </QuoteValue>
          </QuoteRow>}
        </>}
    </QuoteBox>
  }

  /** One quiet line under the card: rate, fee and total, opening into the full breakdown. */
  const renderQuoteLine = (): ReactElement | null => {
    if (!hasAmount) {
      // A pay-side amount that does not reach past the fixed costs sizes nothing.
      return cryptoPaySideTyped && Number(payWithAmount) > 0
        ? <QuoteStack><InlineError>This amount does not cover the fees.</InlineError></QuoteStack>
        : null
    }
    const chevron = <Chevron fill='var(--ink, #20242d)' style={{
      opacity: 0.35,
      transition: '0.2s ease transform',
      transform: quoteExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
      minWidth: 13,
      width: 13
    }} />
    const toggle = (summary: ReactElement): ReactElement => <QuoteLineButton
      type='button'
      aria-expanded={quoteExpanded}
      $dim={isEstimatingFee || isQuotingBank}
      onClick={() => setQuoteExpanded(expanded => !expanded)}>
      {summary}
      {chevron}
    </QuoteLineButton>
    const skeleton = <LoadingBar style={{ borderRadius: 25, width: 220, maxWidth: '100%', height: 18 }} />

    if (flow.kind === 'bankWithdrawal') {
      if (bankQuoteError) return renderQuoteDetails()
      if (!bankEstimate) return isQuotingBank ? skeleton : null
      return <QuoteStack>
        {toggle(<span>
          Platform fee {formatAssetAmount(bankEstimate.platformFee, bankEstimate.sourceCurrency)}{' '}
          {bankEstimate.sourceCurrency}
          {' · '}Bank fee {formatAssetAmount(bankEstimate.providerFee, bankEstimate.sourceCurrency)}{' '}
          {bankEstimate.sourceCurrency}
          {bankEstimate.indicative && ' · Indicative rate'}
        </span>)}
        {quoteExpanded && renderQuoteDetails()}
      </QuoteStack>
    }

    // A converted withdrawal is priced at review; until then the price feed sizes it.
    if (!needsQuote) {
      if (!convertFundingTicker || !sentTicker) return null
      const fundingPrice = priceOf(convertFundingTicker)
      const deliveryPrice = priceOf(sentTicker)
      return <QuoteStack>
        {fundingPrice && deliveryPrice && <QuoteNote style={{ color: 'var(--text-muted, #6b7280)' }}>
          1 {convertFundingTicker} ≈ {formatRate(fundingPrice / deliveryPrice)} {sentTicker} · Priced at review
        </QuoteNote>}
        <QuoteNote>
          Your {convertFundingTicker} is converted to {sentTicker} right before it is sent.
          The rate and cost are priced at review.
        </QuoteNote>
        {convertHasInsufficientFunding && <InlineError>
          Your {convertFundingTicker} balance is lower than this transfer costs.
        </InlineError>}
      </QuoteStack>
    }

    const networkFeeText = sentTicker && cryptoNetworkFee !== undefined
      ? <>Network fee ≈ {formatAssetAmount(cryptoNetworkFee, sentTicker)} {sentTicker}</>
      : null
    const summary = ((): ReactElement | null => {
      if (!feeEstimate || !quoteIsForPair) return null
      // A quote for another amount lends only what does not depend on it.
      if (!quoteIsCurrent) {
        return <span>
          {networkFeeText && <>{networkFeeText}{' · '}</>}
          Updating the quote
        </span>
      }
      if (isEurRecipient) {
        return <span>
          1 {payUnit} ≈ {formatQuoteAmount(feeEstimate.exchangeRate, 4)} EUR
          {' · '}Fee {formatQuoteAmount(feeEstimate.estimatedFeeUsd)} {payUnit}
          {' · '}All fees included
        </span>
      }
      if (payFundingTicker && sentTicker && fundingKind) {
        // The quote's own rate with the listed cost taken out; the market
        // rate stands in, and says so, while the exchange has not priced the pair.
        return <span>
          {quotedRate !== undefined
            ? <>1 {payFundingTicker} ≈ {formatRate(quotedRate)} {sentTicker}{' · '}</>
            : marketRate !== undefined && <>Market rate 1 {payFundingTicker} ≈ {formatRate(marketRate)} {sentTicker}{' · '}</>}
          {fundingKind === 'converted'
            ? networkFeeText
            : quotedFundingAmount !== undefined
              ? <>Conversion cost ≈ {formatAssetAmount(quotedConversionCost, payFundingTicker)} {payFundingTicker}</>
              : <>Costs are locked at review</>}
          {quotedFundingAmount !== undefined && <>
            {' · '}Total ≈ {formatAssetAmount(quotedFundingAmount, payFundingTicker)} {payFundingTicker}
            {cryptoPaySideTyped && <>{' · '}Recipient amount is exact</>}
          </>}
        </span>
      }
      return <span>
        {networkFeeText}
        {networkFeeText && ' · '}
        {feeOnTop
          ? <>Total ≈ {formatAssetAmount(estimatedTotalCrypto, sentTicker)} {sentTicker}</>
          : <>Recipient gets ≈ {formatAssetAmount(recipientGetsCrypto, sentTicker)} {sentTicker}</>}
      </span>
    })()

    return <QuoteStack>
      {isEstimatingFee && !quoteIsForPair
        ? skeleton
        : summary
          ? toggle(summary)
          : <QuoteNote>
            {isEurRecipient
              ? 'A live quote appears here once the amount can be priced.'
              : 'The network fee is charged on-chain and shows in the transaction.'}
          </QuoteNote>}
      {quoteExpanded && quoteIsCurrent && renderQuoteDetails()}
      {cashHasInsufficientFunding && <InlineError>
        Available {cashFundingCurrency} balance is lower than the estimated debit.
      </InlineError>}
      {hedgedFunding && selectedDeliveryMethod && <QuoteNote>
        {selectedDeliveryMethod.ticker} is bought with your {hedgedFunding} when you send.
        The exact rate and cost are locked at review.
      </QuoteNote>}
      {convertFundingTicker && selectedDeliveryMethod && <QuoteNote>
        Your {convertFundingTicker} is converted to {selectedDeliveryMethod.ticker} right before it is sent.
        The rate and cost are priced at review.
      </QuoteNote>}
      {convertHasInsufficientFunding && convertFundingTicker && <InlineError>
        Your {convertFundingTicker} balance is lower than this transfer costs.
      </InlineError>}
      {hedgedHasInsufficientFunding && hedgedFunding && <InlineError>
        Your {hedgedFunding} balance is lower than this payout costs.
      </InlineError>}
    </QuoteStack>
  }

  /**
   * One row of the amount card. The typed row carries the input; the other
   * shows the live counter-amount and, where both sides can be typed, is a
   * click away from taking the caret.
   */
  const renderAmountRow = (side: 'pay' | 'recipient', single = false): ReactElement => {
    const isTyped = single || typedSide === side
    const method = side === 'pay' ? payMethod : recipientMethod
    const ticker = method?.ticker
    const fiatRow = !!method && isFiatMethod(method)
    const label = single
      ? (isEurRecipient ? 'Recipient gets' : 'You send')
      : side === 'pay' ? 'You pay' : 'Recipient gets'
    const counter = side === 'pay' ? payCounterAmount : recipientCounterAmount
    const counterPending = side === 'pay' ? payCounterPending : recipientCounterPending
    const counterText = counter === undefined
      ? undefined
      : fiatRow ? formatQuoteAmount(counter, 2) : formatAssetAmount(counter, ticker)
    // A recipient amount sized from the pay side is the request value itself
    // once its quote is in; until then, and on every quoted side, an estimate.
    const counterIsEstimate = !(cryptoPaySideTyped && side === 'recipient' && quoteIsCurrent)
    // The balance and Max belong to the paying asset: the pay row, or the one row of a same-asset send.
    const carriesBalance = single ? !isEurRecipient : side === 'pay'
    const balanceTicker = selectedFundingMethod?.ticker
    const balanceAmount = carriesBalance && selectedFundingAsset && balanceTicker
      ? formatAssetAmount(floorAssetAmount(balanceTicker, selectedFundingAsset.maxAmount), balanceTicker)
      : undefined
    const fundingPicker = single ? renderFundingTextPicker() : null
    // "Pay with USDC · 4,867.92 available", or "Available 4,867.92 USDC" where the asset needs no picker.
    const balanceText = balanceAmount === undefined
      ? undefined
      : fundingPicker ? `${balanceAmount} available` : `Available ${balanceAmount} ${selectedFundingMethod?.ticker ?? ''}`.trim()
    // The row whose asset is the one going out shows its value in the reporting currency (crypto sends only).
    const approx = (isCryptoDelivery || flow.kind === 'bankWithdrawal') && ticker && ticker === sentTicker && !fiatRow
      ? approxFiatValue(sentTicker, amount)
      : undefined

    return <AmountRow
      $active={isTyped}
      $interactive={!isTyped && canSwitchSide}
      onClick={() => {
        if (isTyped) amountInputRef.current?.focus()
        else if (canSwitchSide) switchTypedSide()
      }}>
      <AmountRowHeader>
        <AmountRowLabel>{label}</AmountRowLabel>
        {balanceText && <AmountRowMeta $tone={balanceExceeded ? 'error' : 'neutral'}>
          {fundingPicker && <>{fundingPicker}<span aria-hidden='true'>·</span></>}
          <span>{balanceText}</span>
          {showMax && isTyped && maxTypedValue && <>
            <span aria-hidden='true'>·</span>
            <MaxTextButton
              type='button'
              disabled={isSubmitting}
              onClick={e => {
                e.stopPropagation()
                handleAmountTyped(maxTypedValue)
              }}>
              Max
            </MaxTextButton>
          </>}
        </AmountRowMeta>}
      </AmountRowHeader>
      <AmountRowBody>
        {renderAssetChip(side)}
        <AmountColumn>
          {isTyped
            ? <AmountInput
              ref={amountInputRef}
              value={typedAmountValue}
              type='text'
              inputMode='decimal'
              autoComplete='off'
              autoCorrect='off'
              spellCheck={false}
              maxLength={30}
              placeholder='0'
              aria-label={ticker ? `${label}, in ${ticker}` : label}
              disabled={isSubmitting}
              onChange={e => handleAmountTyped(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.defaultPrevented) {
                  e.preventDefault()
                  void goToReview()
                }
              }} />
            : counterPending && counterText === undefined
              ? <LoadingBar style={{ borderRadius: 8, height: 26, maxWidth: '100%', width: 120 }} />
              : <CounterAmount $placeholder={counterText === undefined} $dim={counterPending} aria-live='polite'>
                {counterText === undefined ? '0' : counterIsEstimate ? `≈ ${counterText}` : counterText}
              </CounterAmount>}
          {approx !== undefined && <AmountSub>≈ {formatQuoteAmount(approx, 2)} {defaultCurrency}</AmountSub>}
        </AmountColumn>
      </AmountRowBody>
    </AmountRow>
  }

  const renderAmountEntry = (): ReactElement => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <AmountCard>
        {hasTwoRows
          ? <>
            {renderAmountRow('pay')}
            <RowDivider>
              {canSwitchSide
                ? <SwapSidesButton
                  type='button'
                  aria-label={typedSide === 'pay' ? 'Enter what the recipient gets instead' : 'Enter what you pay instead'}
                  title={typedSide === 'pay' ? 'Enter what the recipient gets instead' : 'Enter what you pay instead'}
                  disabled={isSubmitting}
                  onClick={switchTypedSide}>
                  <SwapArrows />
                </SwapSidesButton>
                : <DirectionGlyph aria-hidden='true'><DownArrow /></DirectionGlyph>}
            </RowDivider>
            {renderAmountRow('recipient')}
          </>
          : renderAmountRow('recipient', true)}
      </AmountCard>
      {renderQuoteLine()}
    </div>
  )

  /**
   * A pasted address or IBAN is saved before it is paid. The merchant says
   * who it belongs to: a contact they pay (the payout rail, with the account
   * saved on that contact) or one of their own accounts (the withdrawal rail,
   * beneficiary locked to the organization). Either way the existing account
   * drawers collect everything the rail needs, prefilled with the paste.
   */
  const renderSaveDestinationCard = (
    value: string,
    oneOffKind: OneOffKind,
    candidates: string[]
  ): ReactElement => {
    const isIban = oneOffKind === 'iban'
    const what = isIban ? `bank account ····${value.slice(-4)}` : 'address'
    const query = saveContactSearch.trim().toLowerCase()
    const matches = customers
      .filter(c => !query || [c.name, c.email, c.companyName].some(term => term?.toLowerCase().includes(query)))
      .slice(0, 6)
    const begin = (target: 'contact' | 'own'): void => {
      setPendingSave({ value, oneOffKind, candidates, target })
      setSaveContactSearch('')
      if (target === 'own') {
        setCreateAccountType(isIban ? AccountType.Fiat : AccountType.Crypto)
      }
    }
    return <RecipientCard>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <RecipientCardTitle>Who does this {what} belong to?</RecipientCardTitle>
        <RecipientCardHint>
          It is saved first, so every send to it carries the right name{isIban ? ' and bank details' : ' and network'}.
        </RecipientCardHint>
      </div>

      <ChoiceGrid role='group' aria-label='Who this destination belongs to'>
        <ChoiceCard
          type='button'
          $active={pendingSave?.target === 'contact'}
          disabled={!canSaveIbanRecipients}
          onClick={() => begin('contact')}>
          <ChoiceTitle>Someone I’m paying</ChoiceTitle>
          <ChoiceHint>Saved on a contact and sent as a payout.</ChoiceHint>
        </ChoiceCard>
        <ChoiceCard
          type='button'
          $active={pendingSave?.target === 'own'}
          disabled={!canWriteAccounts}
          onClick={() => begin('own')}>
          <ChoiceTitle>My own account</ChoiceTitle>
          <ChoiceHint>Saved under {User.organization?.name || 'your organization'} and sent as a withdrawal.</ChoiceHint>
        </ChoiceCard>
      </ChoiceGrid>
      {!canSaveIbanRecipients && !canWriteAccounts && <RecipientCardHint>
        Your role can’t save destinations. Ask an admin for the WriteCustomers, WriteExternalAccount or WriteWithdrawalAccount permission.
      </RecipientCardHint>}

      {pendingSave?.target === 'contact' && pendingSave.value === value && <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <TextInput
          value={saveContactSearch}
          placeholder='Search contacts by name or email'
          autoFocus
          onChange={e => setSaveContactSearch((e.target as HTMLInputElement).value)} />
        {matches.length > 0 && <ChoiceList>
          {matches.map(customer => <ChoiceListItem
            key={customer.id}
            type='button'
            onClick={() => setNewRecipient(customer)}>
            <RecipientAvatar name={customer.name || customer.companyName || customer.email} size={28} />
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span>{customer.name || customer.companyName || customer.email}</span>
              {customer.email && <ChoiceHint>{customer.email}</ChoiceHint>}
            </span>
          </ChoiceListItem>)}
        </ChoiceList>}
        <QuickActionPill type='button' onClick={() => setIsNewRecipientOpen(true)} style={{ alignSelf: 'flex-start' }}>
          <QuickActionGlyph>+</QuickActionGlyph> New contact
        </QuickActionPill>
      </div>}
    </RecipientCard>
  }

  const destinationChosen = flow.kind !== 'idle'
  const flowIsActive = destinationChosen &&
    flow.kind !== 'blocked' &&
    flow.kind !== 'customerPending' &&
    flow.kind !== 'saveDestination'
  const showAmountArea = flowIsActive &&
    (flow.kind === 'fiatPayout' || eligibleFundingAssets.length > 0)

  const renderFormStep = (): ReactElement => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <SectionLabel>To</SectionLabel>
        <DestinationPicker
          accounts={accounts}
          customers={customers}
          isLoading={isLoadingRecipients}
          canReadAccounts={canReadAccounts}
          canReadCustomers={canReadCustomers}
          selected={destination}
          recent={recentDestinations}
          onSelect={d => {
            pendingAmountFocus.current = true
            setDestination(d)
          }}
          onClear={() => setDestination(undefined)} />
        {!destination && (canSaveIbanRecipients || canWriteAccounts || (canReadAccounts && accounts.length > 0)) &&
          <QuickActionsRow>
            {canSaveIbanRecipients &&
              <QuickActionPill type='button' onClick={() => setIsNewRecipientOpen(true)}>
                <QuickActionGlyph>+</QuickActionGlyph> New recipient
              </QuickActionPill>}
            {canWriteAccounts &&
              <QuickActionPill type='button' onClick={() => setIsOwnAccountChooserOpen(true)}>
                <QuickActionGlyph>+</QuickActionGlyph> Add your own account
              </QuickActionPill>}
            {canReadAccounts && accounts.length > 0 &&
              <QuickActionPill type='button' onClick={() => navigate('/accounts')}>
                Manage your accounts
              </QuickActionPill>}
          </QuickActionsRow>}
      </div>

      {flow.kind === 'blocked' && <BlockerNotice>{flow.reason}</BlockerNotice>}

      {flow.kind === 'saveDestination' && renderSaveDestinationCard(flow.value, flow.oneOffKind, flow.candidates)}

      {(flow.kind === 'customerPending' || destination?.kind === 'customer') &&
        flow.kind !== 'blocked' &&
        renderDeliverySelect()}

      {flowIsActive && <StepReveal key={destination ? destinationKey(destination) : 'none'}>
        {!showAmountArea && <EmptyState style={{ minHeight: 84 }}>
          {flow.kind === 'ivyCashWithdrawal'
            ? `EUR bank withdrawals are funded with USDC or USDT on ${expectedUsdcNetwork}. Deposit some first from Add funds.`
            : 'No balance can fund this destination yet. Deposit first from Add funds.'}
        </EmptyState>}

        {showAmountArea && renderAmountEntry()}


        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <PrimaryButton
            disabled={blockers.length > 0 || isSubmitting}
            style={{ width: '100%' }}
            isLoading={isQuotingHedged}
            onClick={() => { void goToReview() }}>
            Continue
          </PrimaryButton>
          {shownBlocker && <BlockerNotice>{shownBlocker}</BlockerNotice>}
        </div>
      </StepReveal>}
    </div>
  )

  const renderReviewStep = (): ReactElement => {
    const mode = destination ? destinationMode(destination) : 'payout'
    const recipientName = destination ? destinationDisplayName(destination) : '-'

    return <StepReveal>
      <BackTextButton
        type='button'
        disabled={isSubmitting}
        onClick={() => {
          pendingAmountFocus.current = true
          discardHedgedQuote()
          setConvertQuote(null)
          setStep('form')
        }}>
        <span aria-hidden='true'>←</span> Edit
      </BackTextButton>

      <ReviewIdentity>
        <RecipientAvatar name={recipientName} size={48} />
        <ReviewName>{recipientName}</ReviewName>
        <ReviewDetail>{selectedDestinationDetail()}</ReviewDetail>
      </ReviewIdentity>

      <ReviewAmount aria-label='Amount the recipient gets'>{recipientAmountLabel}</ReviewAmount>

      <QuoteBox>
        {isCryptoDelivery && selectedDeliveryMethod && <QuoteRow>
          <QuoteLabel>{flow.kind === 'cryptoWithdrawal' ? 'Sending' : 'They receive'}</QuoteLabel>
          <QuoteValue>{selectedDeliveryMethod.ticker} on {methodNetworkName(selectedDeliveryMethod)}</QuoteValue>
        </QuoteRow>}
        {flow.kind === 'cryptoPayout' && selectedDeliveryMethod && !hedgedEntry &&
          !!payoutFundingAssetFor(selectedDeliveryMethod) &&
          !PLAIN_PAYOUT_NETWORKS.has(methodNetworkName(selectedDeliveryMethod)) && <QuoteRow>
          <QuoteLabel>Delivery</QuoteLabel>
          <QuoteValue>Bought at the exchange and sent from there</QuoteValue>
        </QuoteRow>}
        {!isEurRecipient && selectedFundingMethod && <QuoteRow>
          <QuoteLabel>Pay with</QuoteLabel>
          <QuoteValue>
            {selectedFundingMethod.ticker}{!unifiedTreasury && <> on{' '}
              {selectedFundingMethod.hostChain?.name ?? selectedFundingMethod.name}</>}
          </QuoteValue>
        </QuoteRow>}
        {isEurRecipient && selectedFundingMethod && <QuoteRow>
          <QuoteLabel>Pay with</QuoteLabel>
          <QuoteValue>{selectedFundingAsset?.convert ? selectedFundingMethod.ticker : cashFundingCurrency}</QuoteValue>
        </QuoteRow>}
        {convertQuote && convertFundingTicker && (selectedDeliveryMethod || isEurRecipient) && <>
          <QuoteRow>
            <QuoteLabel>Rate</QuoteLabel>
            <QuoteValue>
              1 {convertIntoTicker} ≈ {formatAssetAmount(String(Number(convertQuote.fromAmount) / Number(convertQuote.quotedAmount)), convertFundingTicker)}{' '}
              {convertFundingTicker}
            </QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Converted</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(convertQuote.fromAmount, convertFundingTicker)} {convertFundingTicker}
              {' → '}
              {formatAssetAmount(convertQuote.quotedAmount, convertIntoTicker)}
              {' '}{convertIntoTicker}
            </QuoteValue>
          </QuoteRow>
        </>}
        {hedgedEntry && selectedDeliveryMethod && <>
          <QuoteRow>
            <QuoteLabel>Rate</QuoteLabel>
            <QuoteValue>
              1 {selectedDeliveryMethod.ticker} = {formatAssetAmount(hedgedEntry.lockedRate, hedgedEntry.fundingCurrency)}{' '}
              {hedgedEntry.fundingCurrency}
            </QuoteValue>
          </QuoteRow>
          <QuoteRow>
            <QuoteLabel>Conversion fee</QuoteLabel>
            <QuoteValue>
              {formatAssetAmount(hedgedEntry.conversionFee, hedgedEntry.fundingCurrency)} {hedgedEntry.fundingCurrency}
            </QuoteValue>
          </QuoteRow>
        </>}
        {feeEstimate && !hedgedEntry && <QuoteRow>
          <QuoteLabel>{isEurRecipient ? 'Fee' : 'Estimated network fee'}</QuoteLabel>
          <QuoteValue>
            {isEurRecipient
              ? `${formatQuoteAmount(feeEstimate.estimatedFeeUsd)} ${payUnit}`
              : `${formatAssetAmount(String(feeEstimate.estimatedFeeUsd), sentTicker)} ${sentTicker ?? ''}`.trim()}
          </QuoteValue>
        </QuoteRow>}
        {isEurRecipient && feeEstimate?.exchangeRate != null && <QuoteRow>
          <QuoteLabel>Rate</QuoteLabel>
          <QuoteValue>1 {payUnit} ≈ {formatQuoteAmount(feeEstimate.exchangeRate, 8)} EUR</QuoteValue>
        </QuoteRow>}
        <QuoteRow>
          <QuoteLabel>{totalLabel}</QuoteLabel>
          <QuoteValue>{youPayLabel}</QuoteValue>
        </QuoteRow>
        <QuoteRow>
          <QuoteLabel>Books as</QuoteLabel>
          <QuoteValue>
            <InlinePill>{mode === 'withdrawal' ? 'Withdrawal' : 'Payout'}</InlinePill>
          </QuoteValue>
        </QuoteRow>
      </QuoteBox>
      {convertQuote && convertFundingTicker && selectedDeliveryMethod && <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12.5, lineHeight: 1.5 }}>
        The conversion runs at the live rate when you send, sized with a small buffer so the full amount goes out.
        Anything left over stays in your {selectedDeliveryMethod.ticker} balance, and the conversion shows
        separately in your history.
      </span>}
      {flow.kind === 'cryptoWithdrawal' && !convertQuote && <span style={{ color: 'var(--text-faint, #8a919e)', fontSize: 12.5, lineHeight: 1.5 }}>
        The network fee is charged on-chain and shows in the transaction.
      </span>}

      <div ref={reviewActionsRef} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <PrimaryButton
          disabled={!canSend}
          isLoading={isSubmitting}
          style={{ width: '100%' }}
          onClick={submitSend}>
          {isSubmitting ? 'Sending…' : 'Send'}
        </PrimaryButton>
        {shownBlocker && <BlockerNotice>{shownBlocker}</BlockerNotice>}
      </div>
    </StepReveal>
  }

  const renderSuccessStep = (info: SuccessInfo): ReactElement => (
    <StepReveal>
      <SuccessPanel role='status'>
        <SuccessBadge><CheckMark /></SuccessBadge>
        <SuccessHeading>{info.heading}</SuccessHeading>
        <SuccessSummary>{info.amountLabel} to {info.recipientName}</SuccessSummary>
        {info.recipientDetail && <SuccessNote>{info.recipientDetail}</SuccessNote>}
        <SuccessNote>{info.note}</SuccessNote>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', paddingTop: 16 }}>
          <PrimaryButton
            onClick={() => {
              setSuccessInfo(undefined)
              setStep('form')
            }}>
            Send another
          </PrimaryButton>
          <SecondaryLinkButton to={info.historyHref}>
            {info.historyLabel}
          </SecondaryLinkButton>
        </div>
      </SuccessPanel>
    </StepReveal>
  )

  const renderSendWorkspace = (): ReactElement => (
    <FlowSurface>
      {step === 'form' && renderFormStep()}
      {step === 'review' && renderReviewStep()}
      {step === 'success' && successInfo && renderSuccessStep(successInfo)}
    </FlowSurface>
  )

  if (permissionsLoaded && !canUseView) {
    return <Container>
      <DashboardPage>
        <PageHeaderRow>
          <PageHeader title='Send funds' />
        </PageHeaderRow>
        <EmptyState style={{ minHeight: 160 }}>
          You do not have permission to send funds. Ask an organization admin to grant your role
          access to withdrawals or payouts.
        </EmptyState>
      </DashboardPage>
    </Container>
  }

  return <Container>
    <DashboardPage>
      <PageHeaderRow>
        <PageHeader title='Send funds' />
        <PageActionRow>
          <SecondaryLinkButton
            to={ROUTES.PAYOUTS}
            style={{ fontSize: 13, minHeight: 34, padding: '0 12px' }}>
            Payout history
          </SecondaryLinkButton>
          <InlinePill $tone={testMode ? 'amber' : 'green'}>{testMode ? 'Test mode' : 'Live mode'}</InlinePill>
        </PageActionRow>
      </PageHeaderRow>

      <CenteredWorkspace>
        <SendLayout>
          {renderSendWorkspace()}
          <RecentTransactions
            withdrawals={recentWithdrawals}
            payouts={recentPayouts}
            isLoading={isLoadingActivity}
            onChanged={() => { void loadActivity() }} />
        </SendLayout>
      </CenteredWorkspace>
    </DashboardPage>

    {(createAccountType !== undefined || isOwnAccountChooserOpen) && <CreateWithdrawalAccountModal
      title={createAccountType === AccountType.Fiat
        ? 'Add your own bank account'
        : createAccountType === AccountType.Crypto ? 'Add your own wallet' : 'Add your own account'}
      visible
      presentation='drawer'
      accountType={createAccountType}
      initialCryptoAddress={pendingSave?.target === 'own' && pendingSave.oneOffKind !== 'iban' ? pendingSave.value : undefined}
      initialCryptoMethod={pendingSave?.target === 'own' && pendingSave.candidates.length === 1
        ? getMethod(pendingSave.candidates[0])
        : undefined}
      initialIban={pendingSave?.target === 'own' && pendingSave.oneOffKind === 'iban' ? pendingSave.value : undefined}
      allowedNetworks={pendingSave?.target === 'own' && pendingSave.candidates.length > 0 ? pendingSave.candidates : undefined}
      onClose={() => { setCreateAccountType(undefined); setIsOwnAccountChooserOpen(false); setPendingSave(undefined) }}
      onAccountCreated={() => { setPendingSave(undefined); void handleAccountCreated() }}
    />}

    <Modal
      presentation='drawer'
      title='New recipient'
      visible={isNewRecipientOpen}
      onClose={() => setIsNewRecipientOpen(false)}>
      <div style={{ overflow: 'auto' }}>
        <CustomerForm onCreateCustomer={created => {
          setIsNewRecipientOpen(false)
          setNewRecipient(created)
        }} />
      </div>
    </Modal>

    {newRecipient && <CreateExternalAccountModal
      presentation='drawer'
      customerId={newRecipient.id}
      contact={newRecipient}
      title={`Add a way to pay ${newRecipient.name?.trim() || newRecipient.email}`}
      visible
      forcedCategory={pendingSave?.target === 'contact'
        ? (pendingSave.oneOffKind === 'iban' ? PayoutAccountCategory.Fiat : PayoutAccountCategory.Crypto)
        : undefined}
      preselectedMethod={pendingSave?.target === 'contact' && pendingSave.candidates.length === 1
        ? getMethod(pendingSave.candidates[0])
        : undefined}
      initialCryptoAddress={pendingSave?.target === 'contact' && pendingSave.oneOffKind !== 'iban' ? pendingSave.value : undefined}
      initialIban={pendingSave?.target === 'contact' && pendingSave.oneOffKind === 'iban' ? pendingSave.value : undefined}
      allowedNetworks={pendingSave?.target === 'contact' && pendingSave.candidates.length > 0 ? pendingSave.candidates : undefined}
      onClose={() => {
        setNewRecipient(null)
        void loadRecipients()
      }}
      onAccountCreated={() => {
        // The contact becomes the destination; the account just saved is
        // picked once the contact's accounts load (a single account picks
        // itself either way).
        const customer = newRecipient
        pendingSelectValue.current = pendingSave?.target === 'contact' ? pendingSave.value : undefined
        setNewRecipient(null)
        setPendingSave(undefined)
        void loadRecipients()
        pendingAmountFocus.current = true
        setDestination({ kind: 'customer', customer })
      }}
    />}
  </Container>
})

export default Send
